import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Stage, Layer, Line, Rect, Circle, Text, Group } from 'react-konva';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
    MdOutlineMap, MdGridOn, MdGridOff, MdSave, MdUndo, MdRedo,
    MdZoomIn, MdZoomOut, MdFitScreen, MdDelete, MdLock, MdLockOpen,
    MdAdd, MdEdit, MdClose, MdVisibility, MdVisibilityOff,
    MdDragIndicator, MdSettings, MdContentCopy, MdSearch,
    MdKeyboard, MdFileDownload, MdColorLens
} from 'react-icons/md';
import {
    FaMousePointer, FaArrowsAlt, FaLayerGroup, FaStickyNote,
    FaChevronLeft, FaChevronDown, FaChevronRight, FaUsers
} from 'react-icons/fa';
import useFloorPlanSignalR from '@/hooks/useFloorPlanSignalR';
import {
    SNAP_THRESHOLD, MIN_ZOOM, MAX_ZOOM, ZOOM_STEP,
    LAYER_TYPES, ELEMENT_TYPES, TOOLS
} from './FloorPlanDesigner/FloorPlanConstants';
import {
    findSnapPointInList, extractSnapPoints,
    snapToGrid, calcDistance, calcPolygonArea, pxToUnit, unitToPx
} from './FloorPlanDesigner/FloorPlanUtils';
import {
    SettingsModal, FloorModal, LinkRecordModal, ShortcutHelpModal, ElementTypeConfigModal
} from './FloorPlanDesigner/FloorPlanModals';
import '@/styles/FloorPlanDesigner.scss';
import * as Sentry from '@sentry/react';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const FloorPlanDesignerPage = () => {
    const { propertyId } = useParams();
    const navigate = useNavigate();
    const { apiFetch, user } = useAuth();

    // Core state
    const [propertyName, setPropertyName] = useState('');
    const [floorPlan, setFloorPlan] = useState(null);
    const [floors, setFloors] = useState([]);
    const [activeFloorId, setActiveFloorId] = useState(null);
    const [elements, setElements] = useState([]);
    const [layers, setLayers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    // Tool & interaction state
    const [activeTool, setActiveTool] = useState(TOOLS.Select);
    const [selectedElementId, setSelectedElementId] = useState(null);
    const [drawingState, setDrawingState] = useState(null); // For in-progress drawing
    const [showGrid, setShowGrid] = useState(true);
    const [showWatermark, setShowWatermark] = useState(true);

    // Canvas state
    const [stageScale, setStageScale] = useState(0.5);
    const [stagePosition, setStagePosition] = useState({ x: 50, y: 50 });
    const [isPanning, setIsPanning] = useState(false);
    const [stageSize, setStageSize] = useState({ width: 800, height: 600 });
    const stageRef = useRef(null);
    const containerRef = useRef(null);
    const initialFitDone = useRef(false);

    // Panel state
    const [showLayerPanel, setShowLayerPanel] = useState(true);
    const [showNotesPanel, setShowNotesPanel] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [showFloorModal, setShowFloorModal] = useState(false);
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [showShortcutHelp, setShowShortcutHelp] = useState(false);
    const [linkSearchTerm, setLinkSearchTerm] = useState('');
    const [contextMenu, setContextMenu] = useState(null); // { x, y, elementId }

    // Notes state
    const [notes, setNotes] = useState([]);
    const [newNoteContent, setNewNoteContent] = useState('');

    // Floor modal state
    const [floorModalMode, setFloorModalMode] = useState('add'); // 'add' or 'edit'
    const [floorForm, setFloorForm] = useState({ name: '', floorNumber: 0 });

    // MDA data for linking
    const [rentableUnits, setRentableUnits] = useState([]);
    const [promotionalAreas, setPromotionalAreas] = useState([]);
    const [mediaSpaces, setMediaSpaces] = useState([]);

    // Booking status data for at-a-glance visualisation
    const [promoAreaStatus, setPromoAreaStatus] = useState([]); // { id, isBookedToday, bookedBy, ... }
    const [mediaSpaceStatus, setMediaSpaceStatus] = useState([]);

    // Element type display configs (configurable icons/colours)
    const [elementTypeConfigs, setElementTypeConfigs] = useState([]);
    const [showElementTypeConfigModal, setShowElementTypeConfigModal] = useState(false);

    // Undo/redo
    const [undoStack, setUndoStack] = useState([]);
    const [redoStack, setRedoStack] = useState([]);

    // Watermark elements (from floor below)
    const [watermarkElements, setWatermarkElements] = useState([]);

    // Settings form
    const [settingsForm, setSettingsForm] = useState({
        metersPerPixel: 0.05,
        canvasWidth: 2000,
        canvasHeight: 1500,
        gridSpacing: 20
    });

    // Dimension unit for element property editor
    const [elementDimUnit, setElementDimUnit] = useState('m'); // 'm', 'cm', 'mm'

    // Deleted element IDs to track for batch save
    const [deletedElementIds, setDeletedElementIds] = useState([]);

    // Toolbar accordion state (null = all collapsed, string = name of expanded section)
    const [expandedToolbarSection, setExpandedToolbarSection] = useState(null);

    // Active collaborators via SignalR
    const [activeUsers, setActiveUsers] = useState([]);

    // ========================================================================
    // DATA LOADING
    // ========================================================================

    /** Load property info */
    const loadProperty = useCallback(async () => {
        try {
            const res = await apiFetch('/api/Property/all');
            const data = await res.json();
            if (data.success && data.data) {
                const prop = data.data.find(p => (p.propertyID || p.PropertyID) === parseInt(propertyId));
                if (prop) setPropertyName(prop.propertyName || prop.PropertyName || '');
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error('Error loading property:', e);
        }
    }, [apiFetch, propertyId]);

    /** Load floorplan data */
    const loadFloorPlan = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await apiFetch(`/api/FloorPlan/property/${propertyId}`);
            const data = await res.json();

            if (data.success && data.data) {
                setFloorPlan(data.data);
                setFloors(data.data.floors || []);
                setSettingsForm({
                    metersPerPixel: data.data.metersPerPixel || 0.05,
                    canvasWidth: data.data.canvasWidth || 2000,
                    canvasHeight: data.data.canvasHeight || 1500,
                    gridSpacing: data.data.gridSpacing || 20
                });

                if (data.data.floors?.length > 0) {
                    const firstFloor = data.data.floors[0];
                    setActiveFloorId(firstFloor.id);
                    setLayers(firstFloor.layers || []);
                    await loadFloorElements(firstFloor.id);
                }
            } else {
                // No floorplan exists yet
                setFloorPlan(null);
                setFloors([]);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error('Error loading floorplan:', e);
            toast.error('Error loading floorplan');
        } finally {
            setIsLoading(false);
        }
    }, [apiFetch, propertyId]);

    /** Load elements for a specific floor */
    const loadFloorElements = useCallback(async (floorId) => {
        try {
            const res = await apiFetch(`/api/FloorPlan/floor/${floorId}/elements`);
            const data = await res.json();
            if (data.success) {
                setElements(data.data || []);
                setDeletedElementIds([]);
                setUndoStack([]);
                setRedoStack([]);
                setHasUnsavedChanges(false);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error('Error loading elements:', e);
        }
    }, [apiFetch]);

    /** Load watermark elements from the floor below */
    const loadWatermarkElements = useCallback(async (currentFloorId) => {
        const currentFloor = floors.find(f => f.id === currentFloorId);
        if (!currentFloor) return;

        const floorBelow = floors
            .filter(f => f.sortOrder < currentFloor.sortOrder)
            .sort((a, b) => b.sortOrder - a.sortOrder)[0];

        if (!floorBelow) {
            setWatermarkElements([]);
            return;
        }

        try {
            const res = await apiFetch(`/api/FloorPlan/floor/${floorBelow.id}/elements`);
            const data = await res.json();
            if (data.success) {
                setWatermarkElements(data.data || []);
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            setWatermarkElements([]);
        }
    }, [apiFetch, floors]);

    /** Load MDA reference data for linking */
    const loadReferenceData = useCallback(async () => {
        try {
            const [ruRes, paRes, msRes] = await Promise.all([
                apiFetch(`/api/FloorPlan/property/${propertyId}/rentable-units`),
                apiFetch(`/api/FloorPlan/property/${propertyId}/promotional-areas`),
                apiFetch(`/api/FloorPlan/property/${propertyId}/media-spaces`)
            ]);

            const [ruData, paData, msData] = await Promise.all([
                ruRes.json(), paRes.json(), msRes.json()
            ]);

            if (ruData.success) setRentableUnits(ruData.data || []);
            if (paData.success) setPromotionalAreas(paData.data || []);
            if (msData.success) setMediaSpaces(msData.data || []);
        } catch (e) {
            Sentry.captureException(e);
            console.error('Error loading reference data:', e);
        }
    }, [apiFetch, propertyId]);

    /** Load booking status data for at-a-glance floorplan visualisation */
    const loadBookingStatus = useCallback(async () => {
        try {
            const [paRes, msRes] = await Promise.all([
                apiFetch(`/api/FloorPlan/property/${propertyId}/promotional-area-status`),
                apiFetch(`/api/FloorPlan/property/${propertyId}/media-space-status`)
            ]);
            const [paData, msData] = await Promise.all([paRes.json(), msRes.json()]);
            if (paData.success) setPromoAreaStatus(paData.data || []);
            if (msData.success) setMediaSpaceStatus(msData.data || []);
        } catch (e) {
            Sentry.captureException(e);
            console.error('Error loading booking status:', e);
        }
    }, [apiFetch, propertyId]);

    /** Load element type display configs */
    const loadElementTypeConfigs = useCallback(async () => {
        try {
            const res = await apiFetch(`/api/FloorPlan/property/${propertyId}/element-type-configs`);
            const data = await res.json();
            if (data.success) setElementTypeConfigs(data.data || []);
        } catch (e) {
            Sentry.captureException(e);
            console.error('Error loading element type configs:', e);
        }
    }, [apiFetch, propertyId]);

    // Initial data load
    useEffect(() => {
        loadProperty();
        loadFloorPlan();
        loadReferenceData();
        loadBookingStatus();
        loadElementTypeConfigs();
    }, [loadProperty, loadFloorPlan, loadReferenceData, loadBookingStatus, loadElementTypeConfigs]);

    // Apply the floorplan-active class to parent layout elements on mount and
    // remove it on unmount. This replaces the CSS :has() selector approach in
    // FloorPlanDesigner.scss, which has limited browser support.
    useEffect(() => {
        const elementsToToggle = [
            document.body,
            document.documentElement,
            document.querySelector('.main-content'),
            document.querySelector('.layout-root')
        ].filter(Boolean);

        elementsToToggle.forEach(el => el.classList.add('floorplan-active'));

        return () => {
            elementsToToggle.forEach(el => el.classList.remove('floorplan-active'));
        };
    }, []);

    // Load watermark when active floor changes
    useEffect(() => {
        if (activeFloorId && floors.length > 0) {
            loadWatermarkElements(activeFloorId);
        }
    }, [activeFloorId, floors, loadWatermarkElements]);

    // Responsive canvas resize — re-run when loading/floorPlan changes so the
    // observer is attached once the container element actually mounts in the DOM.
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        // Set initial size immediately so the Stage isn't stuck at the default 800x600
        const rect = container.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
            setStageSize({ width: Math.round(rect.width), height: Math.round(rect.height) });
        }
        const observer = new ResizeObserver(entries => {
            for (const entry of entries) {
                const { width, height } = entry.contentRect;
                setStageSize({ width: Math.round(width), height: Math.round(height) });
            }
        });
        observer.observe(container);
        return () => observer.disconnect();
    }, [isLoading, floorPlan]);

    // Auto-fit canvas to screen on initial load
    useEffect(() => {
        if (initialFitDone.current || !floorPlan || !containerRef.current) return;
        const container = containerRef.current;
        const cw = container.offsetWidth;
        const ch = container.offsetHeight;
        if (cw === 0 || ch === 0) return;
        const pw = settingsForm.canvasWidth;
        const ph = settingsForm.canvasHeight;
        // Fit by width so canvas fills the full container width
        const scale = (cw / pw) * 0.95;
        setStageScale(scale);
        setStagePosition({
            x: (cw - pw * scale) / 2,
            y: Math.max(10, (ch - ph * scale) / 2)
        });
        initialFitDone.current = true;
    }, [floorPlan, stageSize, settingsForm.canvasWidth, settingsForm.canvasHeight]);

    // Warn before closing with unsaved changes
    useEffect(() => {
        const handler = (e) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [hasUnsavedChanges]);

    // Auto-save every 2 minutes when there are unsaved changes
    useEffect(() => {
        if (!hasUnsavedChanges || !activeFloorId) return;
        const timer = setTimeout(() => {
            handleSave();
        }, 120000); // 2 minutes
        return () => clearTimeout(timer);
    }, [hasUnsavedChanges, activeFloorId, elements]);

    // ========================================================================
    // NOTES (loadNotes must be defined before signalRCallbacks references it)
    // ========================================================================

    const loadNotes = useCallback(async (elementId) => {
        try {
            const res = await apiFetch(`/api/FloorPlan/element/${elementId}/notes`);
            const data = await res.json();
            if (data.success) setNotes(data.data || []);
        } catch (e) {
            Sentry.captureException(e);
            console.error('Error loading notes:', e);
        }
    }, [apiFetch]);

    // ========================================================================
    // SIGNALR REAL-TIME COLLABORATION
    // ========================================================================

    const signalRCallbacks = useMemo(() => ({
        onFloorPlanCreated: () => {
            loadFloorPlan();
            toast.info('Floorplan created by another user');
        },
        onSettingsUpdated: (data) => {
            if (data.changedBy === user?.userID) return;
            setSettingsForm(prev => ({ ...prev, ...data.settings }));
            toast.info('Floorplan settings updated by another user');
        },
        onFloorCreated: (data) => {
            if (data.changedBy === user?.userID) return;
            loadFloorPlan();
            toast.info('A new floor was added by another user');
        },
        onFloorUpdated: (data) => {
            if (data.changedBy === user?.userID) return;
            loadFloorPlan();
        },
        onFloorDeleted: (data) => {
            if (data.changedBy === user?.userID) return;
            loadFloorPlan();
            toast.info('A floor was removed by another user');
        },
        onElementsSaved: (data) => {
            if (data.changedBy === user?.userID) return;
            // Reload elements for the affected floor
            if (data.floorId === activeFloorId) {
                loadFloorElements(data.floorId);
                toast.info('Elements updated by another user');
            }
            // Also reload watermark in case the floor below changed
            if (activeFloorId) loadWatermarkElements(activeFloorId);
        },
        onElementDeleted: (data) => {
            if (data.changedBy === user?.userID) return;
            if (data.floorId === activeFloorId) {
                setElements(prev => prev.filter(e => e.id !== data.elementId));
            }
        },
        onLayerUpdated: (data) => {
            if (data.changedBy === user?.userID) return;
            if (data.layer) {
                setLayers(prev => prev.map(l =>
                    l.id === data.layer.id ? { ...l, isVisible: data.layer.isVisible, opacity: data.layer.opacity } : l
                ));
            }
        },
        onNoteChanged: (data) => {
            if (data.changedBy === user?.userID) return;
            // Reload notes if we're viewing the affected element
            const selElem = elements.find(e => e.id === selectedElementId || e._tempId === selectedElementId);
            if (selElem?.id === data.elementId && selElem?.id > 0) {
                loadNotes(selElem.id);
            }
        },
        onActiveUsers: (data) => {
            setActiveUsers(data.users || []);
        },
        onUserJoined: (data) => {
            setActiveUsers(prev => {
                if (prev.some(u => u.userId === data.userId)) return prev;
                return [...prev, { userId: data.userId, userName: data.userName }];
            });
        },
        onUserLeft: (data) => {
            setActiveUsers(prev => prev.filter(u => u.userId !== data.userId));
        }
    }), [user?.userID, activeFloorId, selectedElementId, elements, loadFloorPlan, loadFloorElements, loadWatermarkElements, loadNotes]);

    useFloorPlanSignalR(parseInt(propertyId), signalRCallbacks, !!floorPlan);

    // ========================================================================
    // FLOORPLAN CREATION
    // ========================================================================

    const handleCreateFloorPlan = async () => {
        try {
            const res = await apiFetch('/api/FloorPlan/create', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    propertyId: parseInt(propertyId),
                    name: propertyName || 'Floorplan',
                    metersPerPixel: 0.05,
                    canvasWidth: 2000,
                    canvasHeight: 1500,
                    gridSpacing: 20,
                    userId: user?.userID
                })
            });
            const data = await res.json();
            if (data.success) {
                toast.success('Floorplan created!');
                await loadFloorPlan();
            } else {
                toast.error(data.message || 'Failed to create floorplan');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Error creating floorplan');
        }
    };

    // ========================================================================
    // FLOOR MANAGEMENT
    // ========================================================================

    const handleAddFloor = async () => {
        if (!floorPlan) return;
        try {
            const res = await apiFetch('/api/FloorPlan/floor/create', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    floorPlanId: floorPlan.id,
                    floorNumber: floorForm.floorNumber,
                    name: floorForm.name || `Floor ${floorForm.floorNumber}`
                })
            });
            const data = await res.json();
            if (data.success) {
                toast.success('Floor added!');
                setShowFloorModal(false);
                await loadFloorPlan();
            } else {
                toast.error(data.message || 'Failed to add floor');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Error adding floor');
        }
    };

    const handleUpdateFloor = async () => {
        try {
            const res = await apiFetch('/api/FloorPlan/floor/update', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: activeFloorId,
                    name: floorForm.name,
                    floorNumber: floorForm.floorNumber
                })
            });
            const data = await res.json();
            if (data.success) {
                toast.success('Floor updated!');
                setShowFloorModal(false);
                await loadFloorPlan();
            } else {
                toast.error(data.message || 'Failed to update floor');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Error updating floor');
        }
    };

    const handleDeleteFloor = async (floorId) => {
        if (!window.confirm('Are you sure you want to delete this floor and all its elements?')) return;
        try {
            const res = await apiFetch(`/api/FloorPlan/floor/${floorId}`, { method: 'DELETE' });
            const data = await res.json();
            if (data.success) {
                toast.success('Floor deleted!');
                await loadFloorPlan();
            } else {
                toast.error(data.message || 'Failed to delete floor');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Error deleting floor');
        }
    };

    const handleFloorTabClick = async (floor) => {
        if (hasUnsavedChanges) {
            if (!window.confirm('You have unsaved changes. Switch floor anyway?')) return;
        }
        setActiveFloorId(floor.id);
        setLayers(floor.layers || []);
        setSelectedElementId(null);
        setDrawingState(null);
        await loadFloorElements(floor.id);
    };

    // ========================================================================
    // SAVE
    // ========================================================================

    const handleSave = useCallback(async () => {
        if (!activeFloorId) return;
        setIsSaving(true);
        try {
            const res = await apiFetch(`/api/FloorPlan/floor/${activeFloorId}/elements/save`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    elements: elements.map(el => ({
                        id: el.id || 0,
                        layerType: el.layerType,
                        elementType: el.elementType,
                        label: el.label,
                        geometryJson: typeof el.geometryJson === 'string' ? el.geometryJson : JSON.stringify(el.geometryJson),
                        styleJson: typeof el.styleJson === 'string' ? el.styleJson : JSON.stringify(el.styleJson || {}),
                        zIndex: el.zIndex || 0,
                        rotation: el.rotation || 0,
                        isLocked: el.isLocked || false,
                        linkedRentableUnitId: el.linkedRentableUnitId,
                        linkedPromotionalAreaId: el.linkedPromotionalAreaId,
                        linkedMediaSpaceId: el.linkedMediaSpaceId,
                        linkedAssetId: el.linkedAssetId,
                        areaSqm: el.areaSqm
                    })),
                    deletedElementIds: deletedElementIds,
                    userId: user?.userID
                })
            });
            const data = await res.json();
            if (data.success) {
                toast.success('Floorplan saved!');
                setHasUnsavedChanges(false);
                setDeletedElementIds([]);
                await loadFloorElements(activeFloorId);
            } else {
                toast.error(data.message || 'Failed to save');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Error saving floorplan');
        } finally {
            setIsSaving(false);
        }
    }, [activeFloorId, elements, deletedElementIds, user?.userID, apiFetch, loadFloorElements]);

    // ========================================================================
    // SETTINGS
    // ========================================================================

    const handleSaveSettings = async () => {
        if (!floorPlan) return;
        try {
            const res = await apiFetch('/api/FloorPlan/update', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: floorPlan.id,
                    ...settingsForm,
                    userId: user?.userID
                })
            });
            const data = await res.json();
            if (data.success) {
                toast.success('Settings saved!');
                setShowSettingsModal(false);
                await loadFloorPlan();
            } else {
                toast.error(data.message || 'Failed to save settings');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Error saving settings');
        }
    };

    /** Save element type display config */
    const handleSaveElementTypeConfig = async (elementType, configData) => {
        try {
            const res = await apiFetch(`/api/FloorPlan/property/${propertyId}/element-type-configs`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ elementType, ...configData })
            });
            const data = await res.json();
            if (data.success) {
                toast.success(`${elementType} appearance saved!`);
                await loadElementTypeConfigs();
            } else {
                toast.error(data.message || 'Failed to save config');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Error saving element type config');
        }
    };

    // ========================================================================
    // ELEMENT MANIPULATION
    // ========================================================================

    const pushUndo = useCallback(() => {
        setUndoStack(prev => [...prev.slice(-49), JSON.stringify(elements)]);
        setRedoStack([]);
    }, [elements]);

    const handleUndo = () => {
        if (undoStack.length === 0) return;
        const prev = undoStack[undoStack.length - 1];
        setRedoStack(r => [...r, JSON.stringify(elements)]);
        setUndoStack(u => u.slice(0, -1));
        setElements(JSON.parse(prev));
        setHasUnsavedChanges(true);
    };

    const handleRedo = () => {
        if (redoStack.length === 0) return;
        const next = redoStack[redoStack.length - 1];
        setUndoStack(u => [...u, JSON.stringify(elements)]);
        setRedoStack(r => r.slice(0, -1));
        setElements(JSON.parse(next));
        setHasUnsavedChanges(true);
    };

    const addElement = useCallback((newElement) => {
        pushUndo();
        setElements(prev => [...prev, { ...newElement, _tempId: Date.now() }]);
        setHasUnsavedChanges(true);
    }, [pushUndo]);

    const updateElement = useCallback((id, updates) => {
        pushUndo();
        setElements(prev => prev.map(el =>
            (el.id === id || el._tempId === id) ? { ...el, ...updates } : el
        ));
        setHasUnsavedChanges(true);
    }, [pushUndo]);

    const deleteElement = useCallback((id) => {
        pushUndo();
        const el = elements.find(e => e.id === id || e._tempId === id);
        if (el?.id > 0) {
            setDeletedElementIds(prev => [...prev, el.id]);
        }
        setElements(prev => prev.filter(e => e.id !== id && e._tempId !== id));
        setSelectedElementId(null);
        setHasUnsavedChanges(true);
    }, [elements, pushUndo]);

    /**
     * Pre-computed list of all snap-worthy geometry points extracted from the current
     * element set. Re-computed only when `elements` changes (not on every mouse-move).
     * Passed to `findSnapPointInList` in mouse handlers to avoid N × JSON.parse
     * overhead on high-frequency pointer events.
     */
    const snapPoints = useMemo(() => extractSnapPoints(elements), [elements]);

    // ========================================================================
    // CANVAS EVENT HANDLERS
    // ========================================================================

    const getCanvasPoint = useCallback(() => {
        const stage = stageRef.current;
        if (!stage) return { x: 0, y: 0 };
        const pos = stage.getPointerPosition();
        if (!pos) return { x: 0, y: 0 };
        return {
            x: (pos.x - stagePosition.x) / stageScale,
            y: (pos.y - stagePosition.y) / stageScale
        };
    }, [stageScale, stagePosition]);

    const handleStageMouseDown = useCallback((e) => {
        // If clicking on empty stage area with select tool
        if (e.target === e.target.getStage()) {
            if (activeTool === TOOLS.Pan) {
                setIsPanning(true);
                return;
            }
            setSelectedElementId(null);
        }

        const point = getCanvasPoint(e);
        const gridSize = settingsForm.gridSpacing;
        let x = showGrid ? snapToGrid(point.x, gridSize) : point.x;
        let y = showGrid ? snapToGrid(point.y, gridSize) : point.y;

        // Snap to existing element endpoints using the pre-computed snap point list
        const snap = findSnapPointInList(x, y, snapPoints);
        if (snap.snapped) { x = snap.x; y = snap.y; }

        const toolInfo = ELEMENT_TYPES[activeTool];
        if (!toolInfo) return;

        // Check if the layer for this tool is visible
        const targetLayer = layers.find(l => l.layerType === toolInfo.layer);
        if (targetLayer && !targetLayer.isVisible) {
            toast.warning(`The ${LAYER_TYPES[toolInfo.layer]?.label || toolInfo.layer} layer is hidden. Enable it first.`);
            return;
        }

        switch (activeTool) {
            case TOOLS.Wall:
                if (!drawingState) {
                    setDrawingState({ type: 'wall', x1: x, y1: y });
                } else {
                    addElement({
                        layerType: 'Structural',
                        elementType: 'Wall',
                        geometryJson: JSON.stringify({
                            x1: drawingState.x1, y1: drawingState.y1,
                            x2: x, y2: y, thickness: 6
                        }),
                        styleJson: JSON.stringify({ stroke: '#333333', strokeWidth: 6 }),
                        zIndex: elements.length
                    });
                    // Continue drawing from the endpoint
                    setDrawingState({ type: 'wall', x1: x, y1: y });
                }
                break;

            case TOOLS.Door:
                addElement({
                    layerType: 'Structural',
                    elementType: 'Door',
                    geometryJson: JSON.stringify({ x, y, width: 40, rotation: 0, swingDirection: 'left' }),
                    styleJson: JSON.stringify({ stroke: '#8B4513', strokeWidth: 3, fill: 'rgba(139,69,19,0.1)' }),
                    zIndex: elements.length
                });
                break;

            case TOOLS.Window:
                addElement({
                    layerType: 'Structural',
                    elementType: 'Window',
                    geometryJson: JSON.stringify({ x, y, width: 40, rotation: 0 }),
                    styleJson: JSON.stringify({ stroke: '#87CEEB', strokeWidth: 4 }),
                    zIndex: elements.length
                });
                break;

            case TOOLS.Room:
                if (!drawingState) {
                    setDrawingState({ type: 'room', points: [x, y] });
                } else {
                    const pts = [...drawingState.points, x, y];
                    // Check if close to starting point to close the shape
                    const startX = drawingState.points[0];
                    const startY = drawingState.points[1];
                    const dist = Math.sqrt((x - startX) ** 2 + (y - startY) ** 2);
                    if (pts.length >= 6 && dist < SNAP_THRESHOLD) {
                        const area = calcPolygonArea(pts.slice(0, -2), settingsForm.metersPerPixel);
                        addElement({
                            layerType: 'Structural',
                            elementType: 'Room',
                            geometryJson: JSON.stringify({ points: pts.slice(0, -2), closed: true }),
                            styleJson: JSON.stringify({ stroke: '#555', strokeWidth: 2, fill: 'rgba(200,200,200,0.2)' }),
                            zIndex: elements.length,
                            areaSqm: area
                        });
                        setDrawingState(null);
                    } else {
                        setDrawingState({ ...drawingState, points: pts });
                    }
                }
                break;

            case TOOLS.Column:
                addElement({
                    layerType: 'Structural',
                    elementType: 'Column',
                    geometryJson: JSON.stringify({ x, y, radius: 10 }),
                    styleJson: JSON.stringify({ fill: '#666', stroke: '#333', strokeWidth: 2 }),
                    zIndex: elements.length
                });
                break;

            case TOOLS.Stairs:
            case TOOLS.Elevator:
            case TOOLS.Escalator:
                addElement({
                    layerType: 'Structural',
                    elementType: activeTool,
                    geometryJson: JSON.stringify({ x, y, width: 60, height: 40 }),
                    styleJson: JSON.stringify({ stroke: '#666', strokeWidth: 2, fill: 'rgba(150,150,150,0.3)' }),
                    label: activeTool,
                    zIndex: elements.length
                });
                break;

            case TOOLS.Label: {
                const labelText = prompt('Enter label text:');
                if (labelText) {
                    addElement({
                        layerType: 'Structural',
                        elementType: 'Label',
                        label: labelText,
                        geometryJson: JSON.stringify({ x, y, fontSize: 14, rotation: 0 }),
                        styleJson: JSON.stringify({ fill: '#333' }),
                        zIndex: elements.length
                    });
                }
                break;
            }

            case TOOLS.Marker:
                addElement({
                    layerType: 'Structural',
                    elementType: 'Marker',
                    geometryJson: JSON.stringify({ x, y, radius: 8 }),
                    styleJson: JSON.stringify({ fill: '#E53E3E', stroke: '#C53030', strokeWidth: 2 }),
                    zIndex: elements.length
                });
                break;

            case TOOLS.RentableUnit:
                if (!drawingState) {
                    setDrawingState({ type: 'rentableUnit', points: [x, y] });
                } else {
                    const ruPts = [...drawingState.points, x, y];
                    const startRuX = drawingState.points[0];
                    const startRuY = drawingState.points[1];
                    const ruDist = Math.sqrt((x - startRuX) ** 2 + (y - startRuY) ** 2);
                    if (ruPts.length >= 6 && ruDist < SNAP_THRESHOLD) {
                        const closedPts = ruPts.slice(0, -2);
                        const area = calcPolygonArea(closedPts, settingsForm.metersPerPixel);
                        addElement({
                            layerType: 'RentableUnits',
                            elementType: 'RentableUnit',
                            geometryJson: JSON.stringify({ points: closedPts, closed: true }),
                            styleJson: JSON.stringify({ stroke: '#4299E1', strokeWidth: 2, fill: 'rgba(66,153,225,0.15)' }),
                            zIndex: elements.length,
                            areaSqm: area
                        });
                        setDrawingState(null);
                    } else {
                        setDrawingState({ ...drawingState, points: ruPts });
                    }
                }
                break;

            case TOOLS.PromotionalArea:
                addElement({
                    layerType: 'PromotionalAreas',
                    elementType: 'PromotionalArea',
                    geometryJson: JSON.stringify({ x, y, radius: 15 }),
                    styleJson: JSON.stringify({ fill: 'rgba(72,187,120,0.5)', stroke: '#48BB78', strokeWidth: 2 }),
                    zIndex: elements.length
                });
                break;

            case TOOLS.MediaSpace:
                addElement({
                    layerType: 'MediaSpaces',
                    elementType: 'MediaSpace',
                    geometryJson: JSON.stringify({ x, y, width: 30, height: 20 }),
                    styleJson: JSON.stringify({ fill: 'rgba(237,137,54,0.5)', stroke: '#ED8936', strokeWidth: 2 }),
                    zIndex: elements.length
                });
                break;

            case TOOLS.Asset:
                addElement({
                    layerType: 'Assets',
                    elementType: 'Asset',
                    geometryJson: JSON.stringify({ x, y, radius: 12 }),
                    styleJson: JSON.stringify({ fill: 'rgba(159,122,234,0.5)', stroke: '#9F7AEA', strokeWidth: 2 }),
                    zIndex: elements.length
                });
                break;

            case TOOLS.Restroom:
                addElement({
                    layerType: 'Structural',
                    elementType: 'Restroom',
                    label: 'Restroom',
                    geometryJson: JSON.stringify({ x, y, width: 50, height: 40 }),
                    styleJson: JSON.stringify({ fill: 'rgba(66,153,225,0.2)', stroke: '#4299E1', strokeWidth: 2 }),
                    zIndex: elements.length
                });
                break;

            case TOOLS.FireExit:
                addElement({
                    layerType: 'Structural',
                    elementType: 'FireExit',
                    label: 'Fire Exit',
                    geometryJson: JSON.stringify({ x, y, width: 50, height: 30 }),
                    styleJson: JSON.stringify({ fill: 'rgba(229,62,62,0.15)', stroke: '#E53E3E', strokeWidth: 2 }),
                    zIndex: elements.length
                });
                break;

            case TOOLS.Entrance:
                addElement({
                    layerType: 'Structural',
                    elementType: 'Entrance',
                    label: 'Entrance',
                    geometryJson: JSON.stringify({ x, y, width: 60, height: 30 }),
                    styleJson: JSON.stringify({ fill: 'rgba(72,187,120,0.15)', stroke: '#48BB78', strokeWidth: 2 }),
                    zIndex: elements.length
                });
                break;

            case TOOLS.Parking:
                addElement({
                    layerType: 'Structural',
                    elementType: 'Parking',
                    label: 'Parking',
                    geometryJson: JSON.stringify({ x, y, width: 80, height: 50 }),
                    styleJson: JSON.stringify({ fill: 'rgba(160,174,192,0.15)', stroke: '#A0AEC0', strokeWidth: 2 }),
                    zIndex: elements.length
                });
                break;

            case TOOLS.LoadingDock:
                addElement({
                    layerType: 'Structural',
                    elementType: 'LoadingDock',
                    label: 'Loading Dock',
                    geometryJson: JSON.stringify({ x, y, width: 80, height: 40 }),
                    styleJson: JSON.stringify({ fill: 'rgba(160,174,192,0.2)', stroke: '#718096', strokeWidth: 2 }),
                    zIndex: elements.length
                });
                break;

            case TOOLS.FireHydrant:
                addElement({
                    layerType: 'Assets',
                    elementType: 'FireHydrant',
                    label: 'Fire Hydrant',
                    geometryJson: JSON.stringify({ x, y, width: 36, height: 36 }),
                    styleJson: JSON.stringify({ fill: 'rgba(229,62,62,0.15)', stroke: '#E53E3E', strokeWidth: 2 }),
                    zIndex: elements.length
                });
                break;

            case TOOLS.ATM:
                addElement({
                    layerType: 'Assets',
                    elementType: 'ATM',
                    label: 'ATM',
                    geometryJson: JSON.stringify({ x, y, width: 36, height: 36 }),
                    styleJson: JSON.stringify({ fill: 'rgba(66,153,225,0.15)', stroke: '#4299E1', strokeWidth: 2 }),
                    zIndex: elements.length
                });
                break;

            case TOOLS.InfoKiosk:
                addElement({
                    layerType: 'Assets',
                    elementType: 'InfoKiosk',
                    label: 'Info Kiosk',
                    geometryJson: JSON.stringify({ x, y, width: 36, height: 36 }),
                    styleJson: JSON.stringify({ fill: 'rgba(66,153,225,0.12)', stroke: '#4299E1', strokeWidth: 2 }),
                    zIndex: elements.length
                });
                break;

            case TOOLS.DBBoard:
                addElement({
                    layerType: 'Assets',
                    elementType: 'DBBoard',
                    label: 'DB Board',
                    geometryJson: JSON.stringify({ x, y, width: 36, height: 36 }),
                    styleJson: JSON.stringify({ fill: 'rgba(237,137,54,0.15)', stroke: '#ED8936', strokeWidth: 2 }),
                    zIndex: elements.length
                });
                break;

            case TOOLS.MeteringUnit:
                addElement({
                    layerType: 'Assets',
                    elementType: 'MeteringUnit',
                    label: 'Metering Unit',
                    geometryJson: JSON.stringify({ x, y, width: 36, height: 36 }),
                    styleJson: JSON.stringify({ fill: 'rgba(237,137,54,0.12)', stroke: '#ED8936', strokeWidth: 2 }),
                    zIndex: elements.length
                });
                break;

            case TOOLS.Borehole:
                addElement({
                    layerType: 'Assets',
                    elementType: 'Borehole',
                    label: 'Borehole',
                    geometryJson: JSON.stringify({ x, y, width: 36, height: 36 }),
                    styleJson: JSON.stringify({ fill: 'rgba(49,130,206,0.15)', stroke: '#3182CE', strokeWidth: 2 }),
                    zIndex: elements.length
                });
                break;

            case TOOLS.WaterPoint:
                addElement({
                    layerType: 'Assets',
                    elementType: 'WaterPoint',
                    label: 'Water Point',
                    geometryJson: JSON.stringify({ x, y, width: 36, height: 36 }),
                    styleJson: JSON.stringify({ fill: 'rgba(49,130,206,0.12)', stroke: '#3182CE', strokeWidth: 2 }),
                    zIndex: elements.length
                });
                break;

            case TOOLS.CCTV:
                addElement({
                    layerType: 'Assets',
                    elementType: 'CCTV',
                    label: 'CCTV',
                    geometryJson: JSON.stringify({ x, y, width: 36, height: 36 }),
                    styleJson: JSON.stringify({ fill: 'rgba(113,128,150,0.15)', stroke: '#718096', strokeWidth: 2 }),
                    zIndex: elements.length
                });
                break;

            case TOOLS.Sensor:
                addElement({
                    layerType: 'Assets',
                    elementType: 'Sensor',
                    label: 'Sensor',
                    geometryJson: JSON.stringify({ x, y, width: 36, height: 36 }),
                    styleJson: JSON.stringify({ fill: 'rgba(159,122,234,0.15)', stroke: '#9F7AEA', strokeWidth: 2 }),
                    zIndex: elements.length
                });
                break;

            case TOOLS.Delete:
                // Deletion handled by clicking on elements
                break;

            default:
                break;
        }
    }, [activeTool, drawingState, elements, layers, settingsForm, showGrid, snapPoints, addElement, getCanvasPoint]);

    const handleStageMouseMove = useCallback((e) => {
        if (isPanning) {
            const stage = stageRef.current;
            if (!stage) return;
            const pos = stage.getPointerPosition();
            if (!pos) return;
            // Pan is handled by Konva's dragBoundFunc
            return;
        }

        if (drawingState && stageRef.current) {
            const point = getCanvasPoint(e);
            const gridSize = settingsForm.gridSpacing;
            let x = showGrid ? snapToGrid(point.x, gridSize) : point.x;
            let y = showGrid ? snapToGrid(point.y, gridSize) : point.y;

            const snap = findSnapPointInList(x, y, snapPoints);
            if (snap.snapped) { x = snap.x; y = snap.y; }

            setDrawingState(prev => prev ? { ...prev, currentX: x, currentY: y } : null);
        }
    }, [isPanning, drawingState, snapPoints, settingsForm.gridSpacing, showGrid, getCanvasPoint]);

    const handleStageMouseUp = () => {
        setIsPanning(false);
    };

    const handleStageDblClick = useCallback(() => {
        if (!drawingState) return;
        if ((drawingState.type === 'room' || drawingState.type === 'rentableUnit') && drawingState.points.length >= 6) {
            const pts = drawingState.points;
            const area = calcPolygonArea(pts, settingsForm.metersPerPixel);
            const isUnit = drawingState.type === 'rentableUnit';
            addElement({
                layerType: isUnit ? 'RentableUnits' : 'Structural',
                elementType: isUnit ? 'RentableUnit' : 'Room',
                geometryJson: JSON.stringify({ points: pts, closed: true }),
                styleJson: JSON.stringify({
                    stroke: isUnit ? '#4299E1' : '#555',
                    strokeWidth: 2,
                    fill: isUnit ? 'rgba(66,153,225,0.15)' : 'rgba(200,200,200,0.2)'
                }),
                zIndex: elements.length,
                areaSqm: area
            });
            setDrawingState(null);
        } else if (drawingState.type === 'wall') {
            setDrawingState(null);
        }
    }, [drawingState, settingsForm.metersPerPixel, elements.length, addElement]);

    const handleWheel = useCallback((e) => {
        e.evt.preventDefault();
        const stage = stageRef.current;
        if (!stage) return;

        const oldScale = stageScale;
        const pointer = stage.getPointerPosition();
        if (!pointer) return;

        const mousePointTo = {
            x: (pointer.x - stagePosition.x) / oldScale,
            y: (pointer.y - stagePosition.y) / oldScale
        };

        const newScale = e.evt.deltaY > 0
            ? Math.max(MIN_ZOOM, oldScale - ZOOM_STEP)
            : Math.min(MAX_ZOOM, oldScale + ZOOM_STEP);

        setStageScale(newScale);
        setStagePosition({
            x: pointer.x - mousePointTo.x * newScale,
            y: pointer.y - mousePointTo.y * newScale
        });
    }, [stageScale, stagePosition]);

    const handleKeyDown = useCallback((e) => {
        // Ignore single-key shortcuts when user is typing in an input or textarea
        const tag = e.target.tagName;
        const isTyping = tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable;

        if (e.key === 'Escape') {
            setDrawingState(null);
            setActiveTool(TOOLS.Select);
            setContextMenu(null);
        }
        if (e.key === 'Delete' && selectedElementId && !isTyping) {
            deleteElement(selectedElementId);
        }
        if (e.ctrlKey && e.key === 'z') { e.preventDefault(); handleUndo(); }
        if (e.ctrlKey && e.key === 'y') { e.preventDefault(); handleRedo(); }
        if (e.ctrlKey && e.key === 's') { e.preventDefault(); handleSave(); }
        if (!isTyping) {
            if (e.key === 'v' || e.key === 'V') { setActiveTool(TOOLS.Select); setDrawingState(null); }
            if (e.key === 'h' || e.key === 'H') { setActiveTool(TOOLS.Pan); setDrawingState(null); }
            if (e.key === 'g' || e.key === 'G') { setShowGrid(prev => !prev); }
            if (e.key === '?' || (e.shiftKey && e.key === '/')) { setShowShortcutHelp(prev => !prev); }
        }
    }, [selectedElementId, deleteElement, handleUndo, handleRedo, handleSave]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    // ========================================================================
    // ZOOM CONTROLS
    // ========================================================================

    const handleZoomIn = () => setStageScale(s => Math.min(MAX_ZOOM, s + ZOOM_STEP));
    const handleZoomOut = () => setStageScale(s => Math.max(MIN_ZOOM, s - ZOOM_STEP));
    const handleFitToScreen = () => {
        if (!containerRef.current || !floorPlan) return;
        const container = containerRef.current;
        const cw = container.offsetWidth;
        const ch = container.offsetHeight;
        const pw = settingsForm.canvasWidth;
        const ph = settingsForm.canvasHeight;
        // Fit by width so canvas fills the container
        const scale = (cw / pw) * 0.95;
        setStageScale(scale);
        setStagePosition({
            x: (cw - pw * scale) / 2,
            y: Math.max(10, (ch - ph * scale) / 2)
        });
    };

    // ========================================================================
    // RIGHT-CLICK CONTEXT MENU
    // ========================================================================

    const handleContextMenu = useCallback((e, elementId) => {
        e.evt.preventDefault();
        const stage = stageRef.current;
        if (!stage) return;
        const pos = stage.getPointerPosition();
        setContextMenu({ x: pos.x, y: pos.y, elementId });
        setSelectedElementId(elementId);
    }, []);

    const closeContextMenu = () => setContextMenu(null);

    const duplicateElement = useCallback((elemId) => {
        const el = elements.find(e => e.id === elemId || e._tempId === elemId);
        if (!el) return;
        let geom;
        try { geom = typeof el.geometryJson === 'string' ? JSON.parse(el.geometryJson) : el.geometryJson; }
        catch (catchError) {
            Sentry.captureException(catchError);
            return;
        }
        // Offset the duplicate by 20px
        const newGeom = { ...geom };
        if (newGeom.x !== undefined) { newGeom.x += 20; newGeom.y += 20; }
        else if (newGeom.x1 !== undefined) { newGeom.x1 += 20; newGeom.y1 += 20; newGeom.x2 += 20; newGeom.y2 += 20; }
        else if (newGeom.points) { newGeom.points = newGeom.points.map((v) => v + 20); }

        addElement({
            layerType: el.layerType,
            elementType: el.elementType,
            label: el.label ? `${el.label} (copy)` : undefined,
            geometryJson: JSON.stringify(newGeom),
            styleJson: typeof el.styleJson === 'string' ? el.styleJson : JSON.stringify(el.styleJson || {}),
            zIndex: elements.length,
            areaSqm: el.areaSqm
        });
    }, [elements, addElement]);

    // ========================================================================
    // EXPORT TO PNG
    // ========================================================================

    const handleExportSVG = () => {
        const cw = settingsForm.canvasWidth;
        const ch = settingsForm.canvasHeight;
        const escXml = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
        const parts = [];

        // Background
        parts.push(`<rect x="0" y="0" width="${cw}" height="${ch}" fill="#ffffff" stroke="#cbd5e0" stroke-width="1"/>`);

        // Grid
        if (showGrid && settingsForm.gridSpacing > 0) {
            const gs = settingsForm.gridSpacing;
            parts.push('<g opacity="0.5">');
            for (let x = 0; x <= cw; x += gs) {
                parts.push(`<line x1="${x}" y1="0" x2="${x}" y2="${ch}" stroke="#e2e8f0" stroke-width="0.5"/>`);
            }
            for (let y = 0; y <= ch; y += gs) {
                parts.push(`<line x1="0" y1="${y}" x2="${cw}" y2="${y}" stroke="#e2e8f0" stroke-width="0.5"/>`);
            }
            parts.push('</g>');
        }

        // Visible elements
        for (const elem of elements) {
            if (!visibleLayerTypes.has(elem.layerType)) continue;
            let geom, style;
            try {
                geom = typeof elem.geometryJson === 'string' ? JSON.parse(elem.geometryJson) : (elem.geometryJson || {});
                style = typeof elem.styleJson === 'string' ? JSON.parse(elem.styleJson || '{}') : (elem.styleJson || {});
            }
            catch (catchError) {
                Sentry.captureException(catchError);
                continue;
            }

            switch (elem.elementType) {
                case 'Wall': {
                    const stroke = style.stroke || '#333';
                    const sw = style.strokeWidth || 6;
                    parts.push(`<line x1="${geom.x1}" y1="${geom.y1}" x2="${geom.x2}" y2="${geom.y2}" stroke="${stroke}" stroke-width="${sw}" stroke-linecap="round"/>`);
                    const dist = calcDistance(geom.x1, geom.y1, geom.x2, geom.y2, settingsForm.metersPerPixel);
                    parts.push(`<text x="${(geom.x1 + geom.x2) / 2}" y="${(geom.y1 + geom.y2) / 2 - 8}" font-size="10" fill="#a0aec0" text-anchor="middle">${dist}m</text>`);
                    break;
                }
                case 'Door': {
                    const w = geom.width || 40;
                    const rot = geom.rotation || 0;
                    parts.push(`<g transform="translate(${geom.x},${geom.y}) rotate(${rot})">`);
                    parts.push(`<line x1="0" y1="0" x2="${w}" y2="0" stroke="${style.stroke || '#8B4513'}" stroke-width="${style.strokeWidth || 3}"/>`);
                    parts.push(`<line x1="0" y1="0" x2="0" y2="${-w}" stroke="${style.stroke || '#8B4513'}" stroke-width="1" stroke-dasharray="4,4"/>`);
                    parts.push(`<path d="M 0 ${-w} A ${w} ${w} 0 0 1 ${w} 0" fill="none" stroke="${style.stroke || '#8B4513'}" stroke-width="1" stroke-dasharray="2,2"/>`);
                    parts.push('</g>');
                    break;
                }
                case 'Window': {
                    const w = geom.width || 40;
                    const rot = geom.rotation || 0;
                    parts.push(`<g transform="translate(${geom.x},${geom.y}) rotate(${rot})">`);
                    parts.push(`<line x1="0" y1="-2" x2="${w}" y2="-2" stroke="#87CEEB" stroke-width="4"/>`);
                    parts.push(`<line x1="0" y1="2" x2="${w}" y2="2" stroke="#87CEEB" stroke-width="1"/>`);
                    parts.push('</g>');
                    break;
                }
                case 'Room':
                case 'RentableUnit': {
                    if (geom.points && geom.points.length >= 4) {
                        const pointsStr = [];
                        for (let i = 0; i < geom.points.length; i += 2) {
                            pointsStr.push(`${geom.points[i]},${geom.points[i + 1]}`);
                        }
                        const tag = geom.closed ? 'polygon' : 'polyline';
                        parts.push(`<${tag} points="${pointsStr.join(' ')}" stroke="${style.stroke || '#555'}" stroke-width="${style.strokeWidth || 2}" fill="${style.fill || 'rgba(200,200,200,0.2)'}"/>`);
                        if (elem.label && geom.points.length >= 4) {
                            const cx = geom.points.filter((_, i) => i % 2 === 0).reduce((a, b) => a + b, 0) / (geom.points.length / 2);
                            const cy = geom.points.filter((_, i) => i % 2 !== 0).reduce((a, b) => a + b, 0) / (geom.points.length / 2);
                            parts.push(`<text x="${cx}" y="${cy}" font-size="11" fill="${style.stroke || '#4299E1'}" text-anchor="middle">${escXml(elem.label)}</text>`);
                        }
                    }
                    break;
                }
                case 'Column': {
                    parts.push(`<circle cx="${geom.x}" cy="${geom.y}" r="${geom.radius || 10}" fill="${style.fill || '#666'}" stroke="${style.stroke || '#333'}" stroke-width="${style.strokeWidth || 2}"/>`);
                    break;
                }
                case 'Stairs':
                case 'Elevator':
                case 'Escalator': {
                    const w = geom.width || 60;
                    const h = geom.height || 40;
                    parts.push(`<g transform="translate(${geom.x},${geom.y})">`);
                    parts.push(`<rect width="${w}" height="${h}" rx="3" fill="${style.fill || 'rgba(150,150,150,0.3)'}" stroke="${style.stroke || '#666'}" stroke-width="${style.strokeWidth || 2}"/>`);
                    parts.push(`<text x="5" y="${h / 2 + 4}" font-size="10" fill="#333">${escXml(elem.label || elem.elementType)}</text>`);
                    parts.push('</g>');
                    break;
                }
                case 'Label': {
                    parts.push(`<text x="${geom.x}" y="${geom.y}" font-size="${geom.fontSize || 14}" fill="${style.fill || '#333'}" transform="rotate(${geom.rotation || 0},${geom.x},${geom.y})">${escXml(elem.label || '')}</text>`);
                    break;
                }
                case 'Marker':
                case 'PromotionalArea':
                case 'Asset': {
                    const r = geom.radius || 12;
                    parts.push(`<circle cx="${geom.x}" cy="${geom.y}" r="${r}" fill="${style.fill || '#E53E3E'}" stroke="${style.stroke || '#C53030'}" stroke-width="${style.strokeWidth || 2}"/>`);
                    if (elem.label) {
                        parts.push(`<text x="${geom.x}" y="${geom.y + r + 14}" font-size="10" fill="#333" text-anchor="middle">${escXml(elem.label)}</text>`);
                    }
                    break;
                }
                case 'MediaSpace': {
                    const w = geom.width || 30;
                    const h = geom.height || 20;
                    parts.push(`<g transform="translate(${geom.x},${geom.y})">`);
                    parts.push(`<rect width="${w}" height="${h}" rx="2" fill="${style.fill || 'rgba(237,137,54,0.5)'}" stroke="${style.stroke || '#ED8936'}" stroke-width="${style.strokeWidth || 2}"/>`);
                    if (elem.label) {
                        parts.push(`<text x="2" y="${h + 14}" font-size="9" fill="#333">${escXml(elem.label)}</text>`);
                    }
                    parts.push('</g>');
                    break;
                }
                case 'Restroom':
                case 'FireExit':
                case 'Entrance':
                case 'Parking':
                case 'LoadingDock': {
                    const w = geom.width || 60;
                    const h = geom.height || 40;
                    const dash = elem.elementType === 'LoadingDock' ? ' stroke-dasharray="6,3"' : '';
                    parts.push(`<g transform="translate(${geom.x},${geom.y})">`);
                    parts.push(`<rect width="${w}" height="${h}" rx="3" fill="${style.fill || 'rgba(200,200,200,0.15)'}" stroke="${style.stroke || '#666'}" stroke-width="${style.strokeWidth || 2}"${dash}/>`);
                    parts.push(`<text x="${w / 2}" y="${h / 2 + 4}" font-size="11" text-anchor="middle" fill="${style.stroke || '#666'}">${escXml(elem.label || elem.elementType)}</text>`);
                    parts.push('</g>');
                    break;
                }
                case 'FireHydrant':
                case 'ATM':
                case 'InfoKiosk':
                case 'DBBoard':
                case 'MeteringUnit':
                case 'Borehole':
                case 'WaterPoint':
                case 'CCTV':
                case 'Sensor': {
                    const fixtureConfig = {
                        FireHydrant: { color: '#E53E3E', bg: 'rgba(229,62,62,0.15)' },
                        ATM: { color: '#4299E1', bg: 'rgba(66,153,225,0.15)' },
                        InfoKiosk: { color: '#4299E1', bg: 'rgba(66,153,225,0.12)' },
                        DBBoard: { color: '#ED8936', bg: 'rgba(237,137,54,0.15)' },
                        MeteringUnit: { color: '#ED8936', bg: 'rgba(237,137,54,0.12)' },
                        Borehole: { color: '#3182CE', bg: 'rgba(49,130,206,0.15)' },
                        WaterPoint: { color: '#3182CE', bg: 'rgba(49,130,206,0.12)' },
                        CCTV: { color: '#718096', bg: 'rgba(113,128,150,0.15)' },
                        Sensor: { color: '#9F7AEA', bg: 'rgba(159,122,234,0.15)' }
                    };
                    const cfg = fixtureConfig[elem.elementType] || { color: '#9F7AEA', bg: 'rgba(159,122,234,0.15)' };
                    const w = geom.width || 36;
                    const h = geom.height || 36;
                    parts.push(`<g transform="translate(${geom.x},${geom.y})">`);
                    parts.push(`<rect width="${w}" height="${h}" rx="4" fill="${style.fill || cfg.bg}" stroke="${style.stroke || cfg.color}" stroke-width="${style.strokeWidth || 2}"/>`);
                    const labelText = elem.label || ELEMENT_TYPES[elem.elementType]?.label || elem.elementType;
                    parts.push(`<text x="${w / 2}" y="${h / 2 + 4}" font-size="8" text-anchor="middle" fill="${cfg.color}">${escXml(labelText)}</text>`);
                    parts.push('</g>');
                    break;
                }
                default:
                    break;
            }
        }

        const svg = [
            '<?xml version="1.0" encoding="UTF-8"?>',
            `<svg xmlns="http://www.w3.org/2000/svg" width="${cw}" height="${ch}" viewBox="0 0 ${cw} ${ch}">`,
            ...parts,
            '</svg>'
        ].join('\n');

        const blob = new Blob([svg], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = `${propertyName || 'floorplan'}-${floors.find(f => f.id === activeFloorId)?.name || 'floor'}.svg`;
        link.href = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success('Floorplan exported as SVG!');
    };

    // ========================================================================
    // NOTES
    // ========================================================================

    const handleAddNote = async () => {
        if (!selectedElementId || !newNoteContent.trim()) return;
        const realId = elements.find(e => e.id === selectedElementId || e._tempId === selectedElementId)?.id;
        if (!realId || realId <= 0) {
            toast.warning('Please save the floorplan first before adding notes.');
            return;
        }
        try {
            const res = await apiFetch('/api/FloorPlan/note/create', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    elementId: realId,
                    content: newNoteContent,
                    userId: user?.userID,
                    userName: user ? `${user.firstName} ${user.lastName}` : 'Unknown'
                })
            });
            const data = await res.json();
            if (data.success) {
                setNewNoteContent('');
                await loadNotes(realId);
                toast.success('Note added!');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Error adding note');
        }
    };

    const handleDeleteNote = async (noteId) => {
        try {
            const res = await apiFetch(`/api/FloorPlan/note/${noteId}`, { method: 'DELETE' });
            const data = await res.json();
            if (data.success) {
                setNotes(prev => prev.filter(n => n.id !== noteId));
                toast.success('Note deleted');
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Error deleting note');
        }
    };

    // Load notes when element is selected
    useEffect(() => {
        if (selectedElementId) {
            const el = elements.find(e => e.id === selectedElementId || e._tempId === selectedElementId);
            if (el?.id > 0) {
                loadNotes(el.id);
                setShowNotesPanel(true);
            } else {
                setNotes([]);
            }
        } else {
            setShowNotesPanel(false);
            setNotes([]);
        }
    }, [selectedElementId, elements, loadNotes]);

    // ========================================================================
    // LAYER TOGGLE
    // ========================================================================

    const toggleLayerVisibility = async (layerId, currentVisibility) => {
        try {
            const res = await apiFetch('/api/FloorPlan/layer/update', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: layerId, isVisible: !currentVisibility })
            });
            const data = await res.json();
            if (data.success) {
                setLayers(prev => prev.map(l =>
                    l.id === layerId ? { ...l, isVisible: !currentVisibility } : l
                ));
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Error toggling layer');
        }
    };

    // ========================================================================
    // CANVAS GRID RENDERING
    // ========================================================================

    const gridLines = useMemo(() => {
        if (!showGrid || settingsForm.gridSpacing <= 0) return [];
        const lines = [];
        const cw = settingsForm.canvasWidth;
        const ch = settingsForm.canvasHeight;
        const gs = settingsForm.gridSpacing;

        for (let x = 0; x <= cw; x += gs) {
            lines.push(<Line key={`gv-${x}`} points={[x, 0, x, ch]} stroke="#e2e8f0" strokeWidth={0.5} />);
        }
        for (let y = 0; y <= ch; y += gs) {
            lines.push(<Line key={`gh-${y}`} points={[0, y, cw, y]} stroke="#e2e8f0" strokeWidth={0.5} />);
        }
        return lines;
    }, [showGrid, settingsForm]);

    // ========================================================================
    // ELEMENT RENDERING
    // ========================================================================

    const visibleLayerTypes = useMemo(() => {
        return new Set(layers.filter(l => l.isVisible).map(l => l.layerType));
    }, [layers]);

    const renderElement = useCallback((elem, isWatermark = false) => {
        const layerType = elem.layerType;
        if (!isWatermark && !visibleLayerTypes.has(layerType)) return null;

        const id = elem.id || elem._tempId;
        const isSelected = selectedElementId === id;
        let geom, style;
        try {
            geom = typeof elem.geometryJson === 'string' ? JSON.parse(elem.geometryJson) : (elem.geometryJson || {});
            style = typeof elem.styleJson === 'string' ? JSON.parse(elem.styleJson || '{}') : (elem.styleJson || {});
        }
        catch (catchError) {
            Sentry.captureException(catchError);
            return null;
        }

        const opacity = isWatermark ? 0.08 : (isSelected ? 1 : 0.9);
        const baseProps = {
            opacity,
            // Always allow listening so locked elements can be selected/unlocked via
            // click or context menu. Only watermark elements are truly non-interactive.
            listening: !isWatermark,
            onClick: isWatermark ? undefined : () => {
                if (activeTool === TOOLS.Delete && !elem.isLocked) {
                    // Prevent deletion of locked elements via click
                    deleteElement(id);
                } else if (activeTool === TOOLS.Select) {
                    // Allow selecting locked elements so the user can inspect/unlock them
                    setSelectedElementId(id);
                }
                closeContextMenu();
            },
            onContextMenu: isWatermark ? undefined : (e) => handleContextMenu(e, id),
            onDragEnd: isWatermark ? undefined : (e) => {
                // Locked elements are not draggable — this handler will not fire for them
                // because draggable is false when isLocked is true.
                const node = e.target;
                // Update geometry based on element type and new position
                if (elem.elementType === 'Wall') {
                    const dx = node.x();
                    const dy = node.y();
                    updateElement(id, {
                        geometryJson: JSON.stringify({
                            ...geom,
                            x1: geom.x1 + dx, y1: geom.y1 + dy,
                            x2: geom.x2 + dx, y2: geom.y2 + dy
                        })
                    });
                    node.position({ x: 0, y: 0 });
                } else if (geom.x !== undefined) {
                    updateElement(id, {
                        geometryJson: JSON.stringify({ ...geom, x: node.x(), y: node.y() })
                    });
                } else if (geom.points) {
                    const dx = node.x();
                    const dy = node.y();
                    const newPts = geom.points.map((v, i) => v + (i % 2 === 0 ? dx : dy));
                    updateElement(id, {
                        geometryJson: JSON.stringify({ ...geom, points: newPts })
                    });
                    node.position({ x: 0, y: 0 });
                }
            },
            draggable: !isWatermark && !elem.isLocked && activeTool === TOOLS.Select
        };

        const selectionStroke = isSelected ? '#E53E3E' : undefined;
        const selectionStrokeWidth = isSelected ? 3 : undefined;

        switch (elem.elementType) {
            case 'Wall':
                return (
                    <Group key={`el-${id}`} {...baseProps}>
                        <Line
                            points={[geom.x1, geom.y1, geom.x2, geom.y2]}
                            stroke={selectionStroke || style.stroke || '#333'}
                            strokeWidth={selectionStrokeWidth || style.strokeWidth || 6}
                            lineCap="round"
                            hitStrokeWidth={12}
                        />
                        {!isWatermark && (
                            <Text
                                x={(geom.x1 + geom.x2) / 2}
                                y={(geom.y1 + geom.y2) / 2 - 16}
                                text={`${calcDistance(geom.x1, geom.y1, geom.x2, geom.y2, settingsForm.metersPerPixel)}m`}
                                fontSize={10}
                                fill={isSelected ? '#E53E3E' : '#a0aec0'}
                                align="center"
                            />
                        )}
                    </Group>
                );

            case 'Door':
                return (
                    <Group key={`el-${id}`} x={geom.x} y={geom.y} rotation={geom.rotation || 0} {...baseProps}>
                        <Line points={[0, 0, geom.width || 40, 0]} stroke={selectionStroke || style.stroke || '#8B4513'} strokeWidth={style.strokeWidth || 3} />
                        <Line points={[0, 0, 0, -(geom.width || 40)]} stroke={style.stroke || '#8B4513'} strokeWidth={1} dash={[4, 4]} />
                        <Line
                            points={(() => {
                                const w = geom.width || 40;
                                const pts = [];
                                for (let a = 0; a <= 90; a += 5) {
                                    const rad = (a * Math.PI) / 180;
                                    pts.push(Math.sin(rad) * w, -Math.cos(rad) * w);
                                }
                                return pts;
                            })()}
                            stroke={style.stroke || '#8B4513'} strokeWidth={1} dash={[2, 2]}
                        />
                    </Group>
                );

            case 'Window':
                return (
                    <Group key={`el-${id}`} x={geom.x} y={geom.y} rotation={geom.rotation || 0} {...baseProps}>
                        <Line points={[0, -2, geom.width || 40, -2]} stroke={selectionStroke || '#87CEEB'} strokeWidth={4} />
                        <Line points={[0, 2, geom.width || 40, 2]} stroke="#87CEEB" strokeWidth={1} />
                    </Group>
                );

            case 'Room':
            case 'RentableUnit':
                return (
                    <Group key={`el-${id}`} {...baseProps}>
                        <Line
                            points={geom.points || []}
                            stroke={selectionStroke || style.stroke || '#555'}
                            strokeWidth={selectionStrokeWidth || style.strokeWidth || 2}
                            fill={style.fill || 'rgba(200,200,200,0.2)'}
                            closed={geom.closed || false}
                        />
                        {!isWatermark && elem.label && geom.points?.length >= 4 && (
                            <Text
                                x={geom.points.filter((_, i) => i % 2 === 0).reduce((a, b) => a + b, 0) / (geom.points.length / 2)}
                                y={geom.points.filter((_, i) => i % 2 !== 0).reduce((a, b) => a + b, 0) / (geom.points.length / 2) - 6}
                                text={elem.label}
                                fontSize={11}
                                fill={style.stroke || '#4299E1'}
                                align="center"
                                offsetX={elem.label.length * 3}
                            />
                        )}
                    </Group>
                );

            case 'Column':
                return (
                    <Circle
                        key={`el-${id}`}
                        x={geom.x} y={geom.y}
                        radius={geom.radius || 10}
                        fill={style.fill || '#666'}
                        stroke={selectionStroke || style.stroke || '#333'}
                        strokeWidth={selectionStrokeWidth || style.strokeWidth || 2}
                        {...baseProps}
                    />
                );

            case 'Stairs':
            case 'Elevator':
            case 'Escalator':
                return (
                    <Group key={`el-${id}`} x={geom.x} y={geom.y} {...baseProps}>
                        <Rect
                            width={geom.width || 60} height={geom.height || 40}
                            fill={style.fill || 'rgba(150,150,150,0.3)'}
                            stroke={selectionStroke || style.stroke || '#666'}
                            strokeWidth={selectionStrokeWidth || style.strokeWidth || 2}
                            cornerRadius={3}
                        />
                        <Text
                            x={5} y={(geom.height || 40) / 2 - 6}
                            text={elem.label || elem.elementType}
                            fontSize={10} fill="#333"
                        />
                    </Group>
                );

            case 'Label':
                return (
                    <Text
                        key={`el-${id}`}
                        x={geom.x} y={geom.y}
                        text={elem.label || ''}
                        fontSize={geom.fontSize || 14}
                        fill={selectionStroke || style.fill || '#333'}
                        rotation={geom.rotation || 0}
                        {...baseProps}
                    />
                );

            case 'Marker':
            case 'Asset':
                return (
                    <Group key={`el-${id}`} x={geom.x} y={geom.y} {...baseProps}>
                        <Circle
                            radius={geom.radius || 12}
                            fill={style.fill || '#E53E3E'}
                            stroke={selectionStroke || style.stroke || '#C53030'}
                            strokeWidth={selectionStrokeWidth || style.strokeWidth || 2}
                        />
                        {!isWatermark && elem.label && (
                            <Text x={-(elem.label.length * 3)} y={(geom.radius || 12) + 4} text={elem.label} fontSize={10} fill="#333" />
                        )}
                    </Group>
                );

            case 'PromotionalArea': {
                const paStatus = elem.linkedPromotionalAreaId
                    ? promoAreaStatus.find(s => s.id === elem.linkedPromotionalAreaId)
                    : null;
                const paBooked = paStatus?.isBookedToday;
                const paCfg = elementTypeConfigs.find(c => c.elementType === 'PromotionalArea');
                const paColor = paBooked ? '#E53E3E' : '#48BB78';
                const paBg = paBooked ? 'rgba(229,62,62,0.2)' : 'rgba(72,187,120,0.2)';
                const paRadius = geom.radius || paCfg?.defaultWidth / 2 || 16;
                return (
                    <Group key={`el-${id}`} x={geom.x} y={geom.y} {...baseProps}>
                        <Circle
                            radius={paRadius}
                            fill={style.fill || (paCfg?.backgroundColor ? (paCfg.backgroundColor + '30') : paBg)}
                            stroke={selectionStroke || style.stroke || paCfg?.color || paColor}
                            strokeWidth={selectionStrokeWidth || style.strokeWidth || 2}
                        />
                        {paStatus && (
                            <Circle
                                radius={5}
                                x={paRadius - 2}
                                y={-(paRadius - 2)}
                                fill={paBooked ? '#E53E3E' : '#48BB78'}
                                stroke="#fff"
                                strokeWidth={1.5}
                            />
                        )}
                        <Text
                            x={-7} y={-7}
                            text={paCfg?.iconEmoji || '📍'}
                            fontSize={14}
                        />
                        {!isWatermark && (
                            <Text
                                x={-(((elem.label || 'Promo Area').length * 3))}
                                y={paRadius + 4}
                                text={elem.label || 'Promo Area'}
                                fontSize={9}
                                fill={paCfg?.color || paColor}
                            />
                        )}
                        {!isWatermark && paBooked && paStatus.bookedBy && (
                            <Text
                                x={-(paStatus.bookedBy.length * 2.5)}
                                y={paRadius + 16}
                                text={paStatus.bookedBy}
                                fontSize={8}
                                fill="#E53E3E"
                                fontStyle="bold"
                            />
                        )}
                    </Group>
                );
            }

            case 'MediaSpace': {
                const msStatus = elem.linkedMediaSpaceId
                    ? mediaSpaceStatus.find(s => s.id === elem.linkedMediaSpaceId)
                    : null;
                const msBooked = msStatus?.isBookedToday;
                const msCfg = elementTypeConfigs.find(c => c.elementType === 'MediaSpace');
                const msColor = msBooked ? '#E53E3E' : '#ED8936';
                const msBg = msBooked ? 'rgba(229,62,62,0.15)' : 'rgba(237,137,54,0.5)';
                const msW = geom.width || msCfg?.defaultWidth || 30;
                const msH = geom.height || msCfg?.defaultHeight || 20;
                return (
                    <Group key={`el-${id}`} x={geom.x} y={geom.y} {...baseProps}>
                        <Rect
                            width={msW} height={msH}
                            fill={style.fill || (msCfg?.backgroundColor ? (msCfg.backgroundColor + '30') : msBg)}
                            stroke={selectionStroke || style.stroke || msCfg?.color || msColor}
                            strokeWidth={selectionStrokeWidth || style.strokeWidth || 2}
                            cornerRadius={2}
                        />
                        {msStatus && (
                            <Circle
                                radius={4}
                                x={msW - 2}
                                y={2}
                                fill={msBooked ? '#E53E3E' : '#48BB78'}
                                stroke="#fff"
                                strokeWidth={1.5}
                            />
                        )}
                        {!isWatermark && (
                            <Text x={2} y={msH + 4} text={elem.label || 'Media Space'} fontSize={9} fill={msCfg?.color || msColor} />
                        )}
                        {!isWatermark && msBooked && msStatus.bookedBy && (
                            <Text x={2} y={msH + 16} text={msStatus.bookedBy} fontSize={8} fill="#E53E3E" fontStyle="bold" />
                        )}
                    </Group>
                );
            }

            case 'Restroom':
                return (
                    <Group key={`el-${id}`} x={geom.x} y={geom.y} {...baseProps}>
                        <Rect
                            width={geom.width || 50} height={geom.height || 40}
                            fill={style.fill || 'rgba(66,153,225,0.2)'}
                            stroke={selectionStroke || style.stroke || '#4299E1'}
                            strokeWidth={selectionStrokeWidth || style.strokeWidth || 2}
                            cornerRadius={3}
                        />
                        <Text x={4} y={(geom.height || 40) / 2 - 6} text="🚻" fontSize={14} />
                        {!isWatermark && (
                            <Text x={2} y={(geom.height || 40) + 4} text={elem.label || 'Restroom'} fontSize={9} fill="#4299E1" />
                        )}
                    </Group>
                );

            case 'FireExit':
                return (
                    <Group key={`el-${id}`} x={geom.x} y={geom.y} {...baseProps}>
                        <Rect
                            width={geom.width || 50} height={geom.height || 30}
                            fill={style.fill || 'rgba(229,62,62,0.15)'}
                            stroke={selectionStroke || style.stroke || '#E53E3E'}
                            strokeWidth={selectionStrokeWidth || style.strokeWidth || 2}
                            cornerRadius={3}
                        />
                        <Text x={4} y={(geom.height || 30) / 2 - 6} text="🔥" fontSize={12} />
                        {!isWatermark && (
                            <Text x={2} y={(geom.height || 30) + 4} text={elem.label || 'Fire Exit'} fontSize={9} fill="#E53E3E" />
                        )}
                    </Group>
                );

            case 'Entrance':
                return (
                    <Group key={`el-${id}`} x={geom.x} y={geom.y} {...baseProps}>
                        <Rect
                            width={geom.width || 60} height={geom.height || 30}
                            fill={style.fill || 'rgba(72,187,120,0.15)'}
                            stroke={selectionStroke || style.stroke || '#48BB78'}
                            strokeWidth={selectionStrokeWidth || style.strokeWidth || 2}
                            cornerRadius={3}
                        />
                        <Text x={4} y={(geom.height || 30) / 2 - 6} text="🚪" fontSize={12} />
                        {!isWatermark && (
                            <Text x={2} y={(geom.height || 30) + 4} text={elem.label || 'Entrance'} fontSize={9} fill="#48BB78" />
                        )}
                    </Group>
                );

            case 'Parking':
                return (
                    <Group key={`el-${id}`} x={geom.x} y={geom.y} {...baseProps}>
                        <Rect
                            width={geom.width || 80} height={geom.height || 50}
                            fill={style.fill || 'rgba(160,174,192,0.15)'}
                            stroke={selectionStroke || style.stroke || '#A0AEC0'}
                            strokeWidth={selectionStrokeWidth || style.strokeWidth || 2}
                            cornerRadius={3}
                        />
                        <Text x={4} y={(geom.height || 50) / 2 - 6} text="🅿️" fontSize={14} />
                        {!isWatermark && (
                            <Text x={2} y={(geom.height || 50) + 4} text={elem.label || 'Parking'} fontSize={9} fill="#718096" />
                        )}
                    </Group>
                );

            case 'LoadingDock':
                return (
                    <Group key={`el-${id}`} x={geom.x} y={geom.y} {...baseProps}>
                        <Rect
                            width={geom.width || 80} height={geom.height || 40}
                            fill={style.fill || 'rgba(160,174,192,0.2)'}
                            stroke={selectionStroke || style.stroke || '#718096'}
                            strokeWidth={selectionStrokeWidth || style.strokeWidth || 2}
                            dash={[6, 3]}
                            cornerRadius={3}
                        />
                        <Text x={4} y={(geom.height || 40) / 2 - 6} text="🚛" fontSize={12} />
                        {!isWatermark && (
                            <Text x={2} y={(geom.height || 40) + 4} text={elem.label || 'Loading Dock'} fontSize={9} fill="#718096" />
                        )}
                    </Group>
                );

            case 'FireHydrant':
            case 'ATM':
            case 'InfoKiosk':
            case 'DBBoard':
            case 'MeteringUnit':
            case 'Borehole':
            case 'WaterPoint':
            case 'CCTV':
            case 'Sensor': {
                const fixtureDefaults = {
                    FireHydrant:  { emoji: '🧯', color: '#E53E3E', bg: 'rgba(229,62,62,0.15)' },
                    ATM:          { emoji: '🏧', color: '#4299E1', bg: 'rgba(66,153,225,0.15)' },
                    InfoKiosk:    { emoji: 'ℹ️', color: '#4299E1', bg: 'rgba(66,153,225,0.12)' },
                    DBBoard:      { emoji: '⚡', color: '#ED8936', bg: 'rgba(237,137,54,0.15)' },
                    MeteringUnit: { emoji: '🔌', color: '#ED8936', bg: 'rgba(237,137,54,0.12)' },
                    Borehole:     { emoji: '💧', color: '#3182CE', bg: 'rgba(49,130,206,0.15)' },
                    WaterPoint:   { emoji: '🚰', color: '#3182CE', bg: 'rgba(49,130,206,0.12)' },
                    CCTV:         { emoji: '📹', color: '#718096', bg: 'rgba(113,128,150,0.15)' },
                    Sensor:       { emoji: '📡', color: '#9F7AEA', bg: 'rgba(159,122,234,0.15)' }
                };
                const defaults = fixtureDefaults[elem.elementType] || { emoji: '📌', color: '#9F7AEA', bg: 'rgba(159,122,234,0.15)' };
                // Apply user-configured overrides
                const userCfg = elementTypeConfigs.find(c => c.elementType === elem.elementType);
                const cfg = {
                    emoji: userCfg?.iconEmoji || defaults.emoji,
                    color: userCfg?.color || defaults.color,
                    bg: userCfg?.backgroundColor ? userCfg.backgroundColor + '30' : defaults.bg
                };
                return (
                    <Group key={`el-${id}`} x={geom.x} y={geom.y} {...baseProps}>
                        <Rect
                            width={geom.width || userCfg?.defaultWidth || 36} height={geom.height || userCfg?.defaultHeight || 36}
                            fill={style.fill || cfg.bg}
                            stroke={selectionStroke || style.stroke || cfg.color}
                            strokeWidth={selectionStrokeWidth || style.strokeWidth || 2}
                            cornerRadius={4}
                        />
                        <Text x={(geom.width || userCfg?.defaultWidth || 36) / 2 - 7} y={(geom.height || userCfg?.defaultHeight || 36) / 2 - 7} text={cfg.emoji} fontSize={14} />
                        {!isWatermark && (
                            <Text x={0} y={(geom.height || userCfg?.defaultHeight || 36) + 4}
                                text={elem.label || ELEMENT_TYPES[elem.elementType]?.label || elem.elementType}
                                fontSize={8} fill={cfg.color} />
                        )}
                    </Group>
                );
            }

            default:
                return null;
        }
    }, [visibleLayerTypes, selectedElementId, activeTool, settingsForm, deleteElement, updateElement, handleContextMenu, elementTypeConfigs, promoAreaStatus, mediaSpaceStatus]);

    // Drawing preview (in-progress wall/room/unit)
    const renderDrawingPreview = useMemo(() => {
        if (!drawingState || drawingState.currentX === undefined) return null;

        if (drawingState.type === 'wall') {
            const dist = calcDistance(drawingState.x1, drawingState.y1, drawingState.currentX, drawingState.currentY, settingsForm.metersPerPixel);
            return (
                <Group>
                    <Line
                        points={[drawingState.x1, drawingState.y1, drawingState.currentX, drawingState.currentY]}
                        stroke="#E53E3E" strokeWidth={6} dash={[8, 4]} opacity={0.7}
                    />
                    <Text
                        x={(drawingState.x1 + drawingState.currentX) / 2}
                        y={(drawingState.y1 + drawingState.currentY) / 2 - 16}
                        text={`${dist}m`}
                        fontSize={12} fill="#E53E3E"
                    />
                </Group>
            );
        }

        if (drawingState.type === 'room' || drawingState.type === 'rentableUnit') {
            const pts = [...drawingState.points, drawingState.currentX, drawingState.currentY];
            return (
                <Line
                    points={pts}
                    stroke={drawingState.type === 'rentableUnit' ? '#4299E1' : '#555'}
                    strokeWidth={2} dash={[6, 3]} opacity={0.7}
                />
            );
        }

        return null;
    }, [drawingState, settingsForm.metersPerPixel]);

    // ========================================================================
    // STATUS BAR HINTS
    // ========================================================================

    const statusHint = useMemo(() => {
        if (drawingState) {
            if (drawingState.type === 'wall') {
                return '🖊️ Click to place next wall point · Double-click or Escape to stop';
            }
            if (drawingState.type === 'room' || drawingState.type === 'rentableUnit') {
                const nPts = drawingState.points.length / 2;
                if (nPts < 3) return `📐 Click to add point ${nPts + 1} · Need at least 3 points`;
                return `📐 ${nPts} points placed · Click near start to close, or double-click to finish`;
            }
        }
        switch (activeTool) {
            case TOOLS.Select: return '🖱️ Click to select · Drag to move · Right-click for options';
            case TOOLS.Pan: return '✋ Drag to pan the canvas';
            case TOOLS.Delete: return '🗑️ Click an element to delete it';
            case TOOLS.Wall: return '🖊️ Click to start drawing a wall';
            case TOOLS.Room: return '📐 Click to start drawing a room polygon';
            case TOOLS.RentableUnit: return '📐 Click to start drawing a rentable unit polygon';
            case TOOLS.Label: return '🏷️ Click to place a text label';
            default:
                if (ELEMENT_TYPES[activeTool]) return `📍 Click to place ${ELEMENT_TYPES[activeTool].label}`;
                return '';
        }
    }, [activeTool, drawingState]);

    const elementCount = useMemo(() => elements.length, [elements]);

    // ========================================================================
    // LINK MODAL (Link elements to MDA records)
    // ========================================================================

    const selectedElement = useMemo(() => {
        return elements.find(e => e.id === selectedElementId || e._tempId === selectedElementId);
    }, [elements, selectedElementId]);

    const handleLinkRecord = (type, recordId) => {
        if (!selectedElementId) return;
        const updates = {};
        if (type === 'rentableUnit') {
            const unit = rentableUnits.find(u => u.propertyRentableUnitID === recordId);
            updates.linkedRentableUnitId = recordId;
            updates.label = unit?.propertyRentableUnitNo || `Unit ${recordId}`;
        } else if (type === 'promotionalArea') {
            const area = promotionalAreas.find(a => a.id === recordId);
            updates.linkedPromotionalAreaId = recordId;
            updates.label = area?.name || `Promo ${recordId}`;
        } else if (type === 'mediaSpace') {
            const space = mediaSpaces.find(s => s.id === recordId);
            updates.linkedMediaSpaceId = recordId;
            updates.label = space?.name || `Media ${recordId}`;
        }
        updateElement(selectedElementId, updates);
        setShowLinkModal(false);
    };

    // ========================================================================
    // RENDER
    // ========================================================================

    if (isLoading) {
        return (
            <div className="floorplan-loading">
                <div className="spinner"></div>
                <p>Loading floorplan designer...</p>
            </div>
        );
    }

    if (!floorPlan) {
        return (
            <div className="floorplan-empty">
                <MdOutlineMap size={64} color="#a0aec0" />
                <h2>No Floorplan Yet</h2>
                <p>Create a floorplan for <strong>{propertyName || `Property ${propertyId}`}</strong></p>
                <button className="btn-primary" onClick={handleCreateFloorPlan}>
                    <MdAdd /> Create Floorplan
                </button>
                <button className="btn-secondary" onClick={() => navigate(-1)}>
                    <FaChevronLeft /> Back
                </button>
            </div>
        );
    }

    return (
        <div className="floorplan-designer">
            {/* Compact Top Bar: back, title, floor tabs, actions */}
            <div className="fp-topbar">
                <div className="fp-topbar-left">
                    <button className="btn-icon" onClick={() => navigate(-1)} title="Back to property">
                        <FaChevronLeft />
                    </button>
                    <div className="fp-topbar-title">
                        <MdOutlineMap size={18} />
                        <span>{propertyName || 'Floorplan'}</span>
                    </div>
                    {hasUnsavedChanges && <span className="unsaved-badge">●</span>}
                    {activeUsers.length > 0 && (
                        <div className="active-users-indicator" title={activeUsers.map(u => u.userName).join(', ')}>
                            <FaUsers size={12} />
                            <span>{activeUsers.length}</span>
                            <div className="active-users-avatars">
                                {activeUsers.slice(0, 3).map(u => (
                                    <span key={u.userId} className="user-avatar" title={u.userName}>
                                        {(u.userName || '?').charAt(0).toUpperCase()}
                                    </span>
                                ))}
                                {activeUsers.length > 3 && <span className="user-avatar more">+{activeUsers.length - 3}</span>}
                            </div>
                        </div>
                    )}
                </div>

                <div className="fp-topbar-floors">
                    {floors.map(floor => (
                        <div
                            key={floor.id}
                            className={`floor-tab ${activeFloorId === floor.id ? 'active' : ''}`}
                            onClick={() => handleFloorTabClick(floor)}
                        >
                            <span>{floor.name}</span>
                            {activeFloorId === floor.id && (
                                <div className="floor-tab-actions">
                                    <button
                                        className="tab-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setFloorModalMode('edit');
                                            setFloorForm({ name: floor.name, floorNumber: floor.floorNumber });
                                            setShowFloorModal(true);
                                        }}
                                        title="Edit floor"
                                    >
                                        <MdEdit size={12} />
                                    </button>
                                    {floors.length > 1 && (
                                        <button
                                            className="tab-btn danger"
                                            onClick={(e) => { e.stopPropagation(); handleDeleteFloor(floor.id); }}
                                            title="Delete floor"
                                        >
                                            <MdDelete size={12} />
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                    <button className="floor-tab add-tab" onClick={() => {
                        setFloorModalMode('add');
                        const maxNum = floors.length > 0 ? Math.max(...floors.map(f => f.floorNumber)) : -1;
                        setFloorForm({ name: '', floorNumber: maxNum + 1 });
                        setShowFloorModal(true);
                    }}>
                        <MdAdd size={14} /> Add
                    </button>
                </div>

                <div className="fp-topbar-right">
                    <button className="btn-icon" onClick={handleUndo} disabled={undoStack.length === 0} title="Undo (Ctrl+Z)">
                        <MdUndo />
                    </button>
                    <button className="btn-icon" onClick={handleRedo} disabled={redoStack.length === 0} title="Redo (Ctrl+Y)">
                        <MdRedo />
                    </button>
                    <div className="topbar-divider" />
                    <button className="btn-icon" onClick={() => setShowGrid(!showGrid)} title={showGrid ? 'Hide Grid (G)' : 'Show Grid (G)'}>
                        {showGrid ? <MdGridOn /> : <MdGridOff />}
                    </button>
                    <button className="btn-icon" onClick={handleExportSVG} title="Export as SVG">
                        <MdFileDownload />
                    </button>
                    <button className="btn-icon" onClick={() => setShowShortcutHelp(true)} title="Keyboard Shortcuts (?)">
                        <MdKeyboard />
                    </button>
                    <button className="btn-icon" onClick={() => setShowSettingsModal(true)} title="Settings">
                        <MdSettings />
                    </button>
                    <button className="btn-icon" onClick={() => setShowElementTypeConfigModal(true)} title="Element Appearance">
                        <MdColorLens />
                    </button>
                    <button className="btn-save" onClick={handleSave} disabled={isSaving || !hasUnsavedChanges}>
                        <MdSave /> {isSaving ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </div>

            {/* Main Content Area - full remaining viewport */}
            <div className="fp-main">
                {/* Canvas fills entire area */}
                <div className="fp-canvas-container" ref={containerRef} onClick={() => setContextMenu(null)}>
                    <Stage
                        ref={stageRef}
                        width={stageSize.width}
                        height={stageSize.height}
                        scaleX={stageScale}
                        scaleY={stageScale}
                        x={stagePosition.x}
                        y={stagePosition.y}
                        draggable={activeTool === TOOLS.Pan || activeTool === TOOLS.Select}
                        onDragEnd={(e) => {
                            if (e.target === stageRef.current) {
                                setStagePosition({ x: e.target.x(), y: e.target.y() });
                            }
                        }}
                        onMouseDown={handleStageMouseDown}
                        onMouseMove={handleStageMouseMove}
                        onMouseUp={handleStageMouseUp}
                        onDblClick={handleStageDblClick}
                        onWheel={handleWheel}
                        onContextMenu={(e) => e.evt.preventDefault()}
                        style={{ cursor: activeTool === TOOLS.Pan ? 'grab' : activeTool === TOOLS.Select ? 'default' : 'crosshair' }}
                    >
                        {/* Canvas Background */}
                        <Layer>
                            <Rect
                                x={0} y={0}
                                width={settingsForm.canvasWidth}
                                height={settingsForm.canvasHeight}
                                fill="#ffffff"
                                stroke="#cbd5e0"
                                strokeWidth={1}
                            />
                        </Layer>

                        {/* Grid */}
                        <Layer listening={false}>
                            {gridLines}
                        </Layer>

                        {/* Watermark (floor below) */}
                        {showWatermark && (
                            <Layer listening={false} opacity={0.08}>
                                {watermarkElements.map(el => renderElement(el, true))}
                            </Layer>
                        )}

                        {/* Main Elements */}
                        <Layer>
                            {elements.map(el => renderElement(el, false))}
                            {renderDrawingPreview}
                        </Layer>
                    </Stage>

                    {/* Floating Left Toolbar */}
                    <div className="fp-toolbar">
                        <div className="toolbar-section">
                            <span className="toolbar-label">Tools</span>
                            <button className={`tool-btn ${activeTool === TOOLS.Select ? 'active' : ''}`}
                                onClick={() => { setActiveTool(TOOLS.Select); setDrawingState(null); }} title="Select / Move (V)">
                                <FaMousePointer />
                            </button>
                            <button className={`tool-btn ${activeTool === TOOLS.Pan ? 'active' : ''}`}
                                onClick={() => { setActiveTool(TOOLS.Pan); setDrawingState(null); }} title="Pan (H)">
                                <FaArrowsAlt />
                            </button>
                            <button className={`tool-btn ${activeTool === TOOLS.Delete ? 'active' : ''}`}
                                onClick={() => { setActiveTool(TOOLS.Delete); setDrawingState(null); }} title="Delete Tool">
                                <MdDelete />
                            </button>
                        </div>

                        {[
                            { name: 'Structure', tools: ['Wall', 'Door', 'Window', 'Room', 'Column', 'Entrance'] },
                            { name: 'Circulation', tools: ['Stairs', 'Elevator', 'Escalator', 'Parking', 'LoadingDock'] },
                            { name: 'Facilities', tools: ['Restroom', 'FireExit', 'FireHydrant', 'ATM', 'InfoKiosk'] },
                            { name: 'Utilities', tools: ['DBBoard', 'MeteringUnit', 'Borehole', 'WaterPoint', 'CCTV', 'Sensor'] },
                            { name: 'Annotate', tools: ['Label', 'Marker'] },
                            { name: 'Commercial', tools: ['RentableUnit', 'PromotionalArea', 'MediaSpace', 'Asset'] }
                        ].map(section => {
                            const isExpanded = expandedToolbarSection === section.name;
                            const isCommercial = section.name === 'Commercial';
                            return (
                                <div key={section.name} className={`toolbar-section ${isExpanded ? 'expanded' : 'collapsed'}`}>
                                    <button
                                        className="toolbar-label-btn"
                                        onClick={() => setExpandedToolbarSection(isExpanded ? null : section.name)}
                                        title={isExpanded ? `Collapse ${section.name}` : `Expand ${section.name}`}
                                    >
                                        <span className="toolbar-label">{section.name}</span>
                                        {isExpanded ? <FaChevronDown size={8} /> : <FaChevronRight size={8} />}
                                    </button>
                                    {isExpanded && section.tools.map(tool => {
                                        const Icon = ELEMENT_TYPES[tool]?.icon;
                                        return (
                                            <button key={tool}
                                                className={`tool-btn ${activeTool === tool ? 'active' : ''}`}
                                                onClick={() => { setActiveTool(tool); setDrawingState(null); }}
                                                title={ELEMENT_TYPES[tool]?.label || tool}
                                                style={isCommercial ? { borderLeft: `3px solid ${LAYER_TYPES[ELEMENT_TYPES[tool]?.layer]?.color || '#ccc'}` } : undefined}>
                                                {Icon && <Icon />}
                                            </button>
                                        );
                                    })}
                                </div>
                            );
                        })}

                        <div className="toolbar-section">
                            <span className="toolbar-label">Zoom</span>
                            <button className="tool-btn" onClick={handleZoomIn} title="Zoom In"><MdZoomIn /></button>
                            <button className="tool-btn" onClick={handleZoomOut} title="Zoom Out"><MdZoomOut /></button>
                            <button className="tool-btn" onClick={handleFitToScreen} title="Fit to Screen"><MdFitScreen /></button>
                            <span className="zoom-label">{Math.round(stageScale * 100)}%</span>
                        </div>
                    </div>

                    {/* Floating Right Panel (Layers + Properties + Notes) */}
                    <div className={`fp-right-panel ${showLayerPanel ? 'open' : ''}`}>
                        <button className="panel-toggle" onClick={() => setShowLayerPanel(!showLayerPanel)} title={showLayerPanel ? 'Collapse panel' : 'Show layers & properties'}>
                            {showLayerPanel ? <MdClose size={14} /> : <FaLayerGroup size={14} />}
                        </button>

                        {showLayerPanel && (
                            <div className="panel-content">
                                <div className="panel-section">
                                    <h3><FaLayerGroup /> Layers</h3>
                                    {layers.sort((a, b) => a.sortOrder - b.sortOrder).map(layer => (
                                        <div key={layer.id} className="layer-item">
                                            <button
                                                className="layer-visibility"
                                                onClick={() => toggleLayerVisibility(layer.id, layer.isVisible)}
                                                title={layer.isVisible ? 'Hide layer' : 'Show layer'}
                                            >
                                                {layer.isVisible ? <MdVisibility /> : <MdVisibilityOff />}
                                            </button>
                                            <span className="layer-color" style={{ background: layer.defaultColor || '#ccc' }}></span>
                                            <span className="layer-name">{layer.displayName}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* Selected Element Info */}
                                {selectedElement && (
                                    <div className="panel-section">
                                        <h3>Selected Element</h3>
                                        <div className="element-info">
                                            <p><strong>Type:</strong> {selectedElement.elementType}</p>
                                            {selectedElement.label && <p><strong>Label:</strong> {selectedElement.label}</p>}
                                            {selectedElement.areaSqm && <p><strong>Area:</strong> {selectedElement.areaSqm.toFixed(2)} m²</p>}

                                            {/* Linked Record Details */}
                                            {selectedElement.linkedRentableUnitId && (() => {
                                                const unit = rentableUnits.find(u => u.propertyRentableUnitID === selectedElement.linkedRentableUnitId);
                                                return unit ? (
                                                    <div className="linked-record-detail">
                                                        <p className="linked-badge">🔗 Linked Rentable Unit</p>
                                                        <p><strong>Unit:</strong> {unit.propertyRentableUnitNo}</p>
                                                        {unit.rudescription && <p><strong>Description:</strong> {unit.rudescription}</p>}
                                                        {unit.area && <p><strong>Area:</strong> {unit.area} m²</p>}
                                                        {unit.nextTenantName && <p><strong>Tenant:</strong> {unit.nextTenantName}</p>}
                                                    </div>
                                                ) : null;
                                            })()}
                                            {selectedElement.linkedPromotionalAreaId && (() => {
                                                const area = promotionalAreas.find(a => a.id === selectedElement.linkedPromotionalAreaId);
                                                return area ? (
                                                    <div className="linked-record-detail">
                                                        <p className="linked-badge">🔗 Linked Promotional Area</p>
                                                        <p><strong>Name:</strong> {area.name}</p>
                                                        {area.locationDescription && <p><strong>Location:</strong> {area.locationDescription}</p>}
                                                    </div>
                                                ) : null;
                                            })()}
                                            {selectedElement.linkedMediaSpaceId && (() => {
                                                const space = mediaSpaces.find(s => s.id === selectedElement.linkedMediaSpaceId);
                                                return space ? (
                                                    <div className="linked-record-detail">
                                                        <p className="linked-badge">🔗 Linked Media Space</p>
                                                        <p><strong>Name:</strong> {space.name}</p>
                                                        {space.locationDescription && <p><strong>Location:</strong> {space.locationDescription}</p>}
                                                    </div>
                                                ) : null;
                                            })()}

                                            {/* Property Editing */}
                                            <div className="property-editor">
                                                <h4><MdColorLens /> Properties</h4>
                                                {(() => {
                                                    let style = {};
                                                    let geom = {};
                                                    try { style = typeof selectedElement.styleJson === 'string' ? JSON.parse(selectedElement.styleJson || '{}') : (selectedElement.styleJson || {}); }
                                                    catch (catchError) {
                                                        Sentry.captureException(catchError);
                                                        /* ignore parse errors */
                                                    }
                                                    try { geom = typeof selectedElement.geometryJson === 'string' ? JSON.parse(selectedElement.geometryJson) : (selectedElement.geometryJson || {}); }
                                                    catch (catchError) {
                                                        Sentry.captureException(catchError);
                                                        /* ignore parse errors */
                                                    }
                                                    const mpp = settingsForm.metersPerPixel;
                                                    const isWall = geom.x1 !== undefined && geom.y1 !== undefined;
                                                    const isPositioned = geom.x !== undefined && geom.y !== undefined;
                                                    const hasDimensions = geom.width !== undefined || geom.height !== undefined || geom.radius !== undefined || isWall || isPositioned;
                                                    return (
                                                        <>
                                                            <label className="prop-label">
                                                                Stroke:
                                                                <input type="color" value={style.stroke || '#333333'}
                                                                    onChange={(e) => updateElement(selectedElementId, {
                                                                        styleJson: JSON.stringify({ ...style, stroke: e.target.value })
                                                                    })} />
                                                            </label>
                                                            {style.fill && (
                                                                <label className="prop-label">
                                                                    Fill:
                                                                    <input type="color" value={style.fill.startsWith('rgba') ? '#cccccc' : (style.fill || '#cccccc')}
                                                                        onChange={(e) => updateElement(selectedElementId, {
                                                                            styleJson: JSON.stringify({ ...style, fill: e.target.value + '40' })
                                                                        })} />
                                                                </label>
                                                            )}
                                                            <label className="prop-label">
                                                                Stroke Width:
                                                                <input type="number" min="1" max="20" value={style.strokeWidth || 2}
                                                                    onChange={(e) => updateElement(selectedElementId, {
                                                                        styleJson: JSON.stringify({ ...style, strokeWidth: parseInt(e.target.value) || 2 })
                                                                    })} />
                                                            </label>

                                                            {/* Measurements section */}
                                                            {hasDimensions && (
                                                                <>
                                                                    <h4>📐 Measurements</h4>
                                                                    <label className="prop-label">
                                                                        Unit:
                                                                        <select value={elementDimUnit} onChange={e => setElementDimUnit(e.target.value)}>
                                                                            <option value="m">Meters</option>
                                                                            <option value="cm">Centimeters</option>
                                                                            <option value="mm">Millimeters</option>
                                                                        </select>
                                                                    </label>
                                                                </>
                                                            )}

                                                            {/* Wall measurements: length, thickness, start/end positions */}
                                                            {isWall && (() => {
                                                                const dx = geom.x2 - geom.x1;
                                                                const dy = geom.y2 - geom.y1;
                                                                const lengthPx = Math.sqrt(dx * dx + dy * dy);
                                                                return (
                                                                    <>
                                                                        <label className="prop-label">
                                                                            Length ({elementDimUnit}):
                                                                            <input type="number" step="any" min="0"
                                                                                value={parseFloat(pxToUnit(lengthPx, mpp, elementDimUnit).toFixed(2))}
                                                                                onChange={(e) => {
                                                                                    const newLenPx = unitToPx(parseFloat(e.target.value) || 0, mpp, elementDimUnit);
                                                                                    if (lengthPx === 0) return;
                                                                                    const scale = newLenPx / lengthPx;
                                                                                    updateElement(selectedElementId, {
                                                                                        geometryJson: JSON.stringify({
                                                                                            ...geom,
                                                                                            x2: geom.x1 + dx * scale,
                                                                                            y2: geom.y1 + dy * scale
                                                                                        })
                                                                                    });
                                                                                }} />
                                                                        </label>
                                                                        <label className="prop-label">
                                                                            Thickness (px):
                                                                            <input type="number" min="1" max="50" value={geom.thickness || 6}
                                                                                onChange={(e) => updateElement(selectedElementId, {
                                                                                    geometryJson: JSON.stringify({ ...geom, thickness: parseInt(e.target.value) || 6 })
                                                                                })} />
                                                                        </label>
                                                                        <label className="prop-label">
                                                                            Start X ({elementDimUnit}):
                                                                            <input type="number" step="any"
                                                                                value={parseFloat(pxToUnit(geom.x1, mpp, elementDimUnit).toFixed(2))}
                                                                                onChange={(e) => {
                                                                                    const newX1 = Math.round(unitToPx(parseFloat(e.target.value) || 0, mpp, elementDimUnit));
                                                                                    updateElement(selectedElementId, {
                                                                                        geometryJson: JSON.stringify({ ...geom, x1: newX1, x2: newX1 + dx })
                                                                                    });
                                                                                }} />
                                                                        </label>
                                                                        <label className="prop-label">
                                                                            Start Y ({elementDimUnit}):
                                                                            <input type="number" step="any"
                                                                                value={parseFloat(pxToUnit(geom.y1, mpp, elementDimUnit).toFixed(2))}
                                                                                onChange={(e) => {
                                                                                    const newY1 = Math.round(unitToPx(parseFloat(e.target.value) || 0, mpp, elementDimUnit));
                                                                                    updateElement(selectedElementId, {
                                                                                        geometryJson: JSON.stringify({ ...geom, y1: newY1, y2: newY1 + dy })
                                                                                    });
                                                                                }} />
                                                                        </label>
                                                                    </>
                                                                );
                                                            })()}

                                                            {/* Position for point-based elements */}
                                                            {isPositioned && (
                                                                <>
                                                                    <label className="prop-label">
                                                                        X ({elementDimUnit}):
                                                                        <input type="number" step="any"
                                                                            value={parseFloat(pxToUnit(geom.x, mpp, elementDimUnit).toFixed(2))}
                                                                            onChange={(e) => {
                                                                                const px = Math.round(unitToPx(parseFloat(e.target.value) || 0, mpp, elementDimUnit));
                                                                                updateElement(selectedElementId, {
                                                                                    geometryJson: JSON.stringify({ ...geom, x: px })
                                                                                });
                                                                            }} />
                                                                    </label>
                                                                    <label className="prop-label">
                                                                        Y ({elementDimUnit}):
                                                                        <input type="number" step="any"
                                                                            value={parseFloat(pxToUnit(geom.y, mpp, elementDimUnit).toFixed(2))}
                                                                            onChange={(e) => {
                                                                                const px = Math.round(unitToPx(parseFloat(e.target.value) || 0, mpp, elementDimUnit));
                                                                                updateElement(selectedElementId, {
                                                                                    geometryJson: JSON.stringify({ ...geom, y: px })
                                                                                });
                                                                            }} />
                                                                    </label>
                                                                </>
                                                            )}
                                                            {geom.width !== undefined && (
                                                                <label className="prop-label">
                                                                    Width ({elementDimUnit}):
                                                                    <input type="number" step="any" min="0"
                                                                        value={parseFloat(pxToUnit(geom.width, mpp, elementDimUnit).toFixed(2))}
                                                                        onChange={(e) => {
                                                                            const px = Math.round(unitToPx(parseFloat(e.target.value) || 0, mpp, elementDimUnit));
                                                                            updateElement(selectedElementId, {
                                                                                geometryJson: JSON.stringify({ ...geom, width: Math.max(1, px) })
                                                                            });
                                                                        }} />
                                                                </label>
                                                            )}
                                                            {geom.height !== undefined && (
                                                                <label className="prop-label">
                                                                    Height ({elementDimUnit}):
                                                                    <input type="number" step="any" min="0"
                                                                        value={parseFloat(pxToUnit(geom.height, mpp, elementDimUnit).toFixed(2))}
                                                                        onChange={(e) => {
                                                                            const px = Math.round(unitToPx(parseFloat(e.target.value) || 0, mpp, elementDimUnit));
                                                                            updateElement(selectedElementId, {
                                                                                geometryJson: JSON.stringify({ ...geom, height: Math.max(1, px) })
                                                                            });
                                                                        }} />
                                                                </label>
                                                            )}
                                                            {geom.radius !== undefined && (
                                                                <label className="prop-label">
                                                                    Radius ({elementDimUnit}):
                                                                    <input type="number" step="any" min="0"
                                                                        value={parseFloat(pxToUnit(geom.radius, mpp, elementDimUnit).toFixed(2))}
                                                                        onChange={(e) => {
                                                                            const px = Math.round(unitToPx(parseFloat(e.target.value) || 0, mpp, elementDimUnit));
                                                                            updateElement(selectedElementId, {
                                                                                geometryJson: JSON.stringify({ ...geom, radius: Math.max(1, px) })
                                                                            });
                                                                        }} />
                                                                </label>
                                                            )}
                                                            {geom.rotation !== undefined && (
                                                                <label className="prop-label">
                                                                    Rotation (°):
                                                                    <input type="number" min="0" max="360" value={geom.rotation}
                                                                        onChange={(e) => updateElement(selectedElementId, {
                                                                            geometryJson: JSON.stringify({ ...geom, rotation: parseInt(e.target.value) || 0 })
                                                                        })} />
                                                                </label>
                                                            )}
                                                        </>
                                                    );
                                                })()}
                                            </div>

                                            <div className="element-actions">
                                                {(selectedElement.elementType === 'RentableUnit' || selectedElement.elementType === 'PromotionalArea' || selectedElement.elementType === 'MediaSpace' || selectedElement.elementType === 'Asset') && (
                                                    <button className="btn-small" onClick={() => setShowLinkModal(true)}>
                                                        Link Record
                                                    </button>
                                                )}
                                                <button className="btn-small" onClick={() => {
                                                    const label = prompt('Enter label:', selectedElement.label || '');
                                                    if (label !== null) updateElement(selectedElementId, { label });
                                                }}>
                                                    <MdEdit /> Label
                                                </button>
                                                <button className="btn-small" onClick={() => duplicateElement(selectedElementId)}>
                                                    <MdContentCopy /> Copy
                                                </button>
                                                <button className="btn-small danger" onClick={() => deleteElement(selectedElementId)}>
                                                    <MdDelete />
                                                </button>
                                                <button className="btn-small" onClick={() =>
                                                    updateElement(selectedElementId, { isLocked: !selectedElement.isLocked })
                                                }>
                                                    {selectedElement.isLocked ? <MdLockOpen /> : <MdLock />}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Notes Panel */}
                                {showNotesPanel && selectedElement?.id > 0 && (
                                    <div className="panel-section notes-section">
                                        <h3><FaStickyNote /> Notes</h3>
                                        <div className="notes-list">
                                            {notes.map(note => (
                                                <div key={note.id} className="note-card">
                                                    <div className="note-header">
                                                        <span className="note-author">{note.createdByUserName || 'Unknown'}</span>
                                                        <span className="note-date">{new Date(note.createdDate).toLocaleDateString()}</span>
                                                        <button className="note-delete" onClick={() => handleDeleteNote(note.id)} title="Delete note">
                                                            <MdClose size={12} />
                                                        </button>
                                                    </div>
                                                    <div className="note-content">
                                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{note.content}</ReactMarkdown>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="note-input">
                                            <textarea
                                                value={newNoteContent}
                                                onChange={e => setNewNoteContent(e.target.value)}
                                                placeholder="Add a note (supports Markdown)..."
                                                rows={3}
                                            />
                                            <button className="btn-small" onClick={handleAddNote} disabled={!newNoteContent.trim()}>
                                                Add Note
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Scale indicator */}
                    <div className="scale-indicator">
                        <div className="scale-bar"></div>
                        <span>{(100 * settingsForm.metersPerPixel).toFixed(1)}m</span>
                        <span className="element-count">{elementCount} element{elementCount !== 1 ? 's' : ''}</span>
                    </div>

                    {/* Status / Drawing Hint Bar */}
                    {statusHint && (
                        <div className="fp-status-bar">
                            {statusHint}
                        </div>
                    )}

                    {/* Context Menu */}
                    {contextMenu && (() => {
                        const ctxElem = elements.find(e => e.id === contextMenu.elementId || e._tempId === contextMenu.elementId);
                        return (
                            <div className="fp-context-menu" style={{ left: contextMenu.x, top: contextMenu.y }}
                                onClick={closeContextMenu}>
                                <button onClick={() => { duplicateElement(contextMenu.elementId); closeContextMenu(); }}>
                                    <MdContentCopy /> Duplicate
                                </button>
                                <button onClick={() => {
                                    const label = prompt('Enter label:', ctxElem?.label || '');
                                    if (label !== null) updateElement(contextMenu.elementId, { label });
                                    closeContextMenu();
                                }}>
                                    <MdEdit /> Rename
                                </button>
                                <button onClick={() => {
                                    updateElement(contextMenu.elementId, { isLocked: !ctxElem?.isLocked });
                                    closeContextMenu();
                                }}>
                                    {ctxElem?.isLocked ? <MdLockOpen /> : <MdLock />}
                                    {ctxElem?.isLocked ? ' Unlock' : ' Lock'}
                                </button>
                                {(ctxElem?.elementType === 'RentableUnit' || ctxElem?.elementType === 'PromotionalArea' || ctxElem?.elementType === 'MediaSpace' || ctxElem?.elementType === 'Asset') && (
                                    <button onClick={() => { setShowLinkModal(true); closeContextMenu(); }}>
                                        <MdDragIndicator /> Link Record
                                    </button>
                                )}
                                <div className="ctx-divider" />
                                <button className="danger" onClick={() => { deleteElement(contextMenu.elementId); closeContextMenu(); }}>
                                    <MdDelete /> Delete
                                </button>
                            </div>
                        );
                    })()}
                </div>

                {/* Settings Modal */}
                {showSettingsModal && (
                    <SettingsModal
                        settingsForm={settingsForm}
                        onSettingsChange={setSettingsForm}
                        showWatermark={showWatermark}
                        onWatermarkChange={setShowWatermark}
                        onSave={handleSaveSettings}
                        onClose={() => setShowSettingsModal(false)}
                    />
                )}

                {/* Floor Add/Edit Modal */}
                {showFloorModal && (
                    <FloorModal
                        mode={floorModalMode}
                        floorForm={floorForm}
                        onFloorFormChange={setFloorForm}
                        onConfirm={floorModalMode === 'add' ? handleAddFloor : handleUpdateFloor}
                        onClose={() => setShowFloorModal(false)}
                    />
                )}

                {/* Link Record Modal */}
                {showLinkModal && selectedElement && (
                    <LinkRecordModal
                        selectedElement={selectedElement}
                        rentableUnits={rentableUnits}
                        promotionalAreas={promotionalAreas}
                        mediaSpaces={mediaSpaces}
                        searchTerm={linkSearchTerm}
                        onSearchChange={setLinkSearchTerm}
                        onLinkRecord={handleLinkRecord}
                        onClose={() => setShowLinkModal(false)}
                    />
                )}

                {/* Keyboard Shortcut Help */}
                {showShortcutHelp && (
                    <ShortcutHelpModal onClose={() => setShowShortcutHelp(false)} />
                )}

                {/* Element Type Appearance Config */}
                {showElementTypeConfigModal && (
                    <ElementTypeConfigModal
                        configs={elementTypeConfigs}
                        elementTypes={Object.keys(ELEMENT_TYPES)}
                        onSave={handleSaveElementTypeConfig}
                        onClose={() => setShowElementTypeConfigModal(false)}
                    />
                )}
            </div>
</div>
    );
};

export default FloorPlanDesignerPage;
